/* global React, Icon, Card, Button, IconButton, Switch, Badge, toast, RESULT, COMPANY, INVOICES, VAT, fmt, fmt0 */
// ============================================================
// PLUMME 2.0 — Skattemelding, Årsavslutning, Årsregnskap
// Functionality adapted from the Plumme reports codebase.
// ============================================================
// year comes from the global year selector (passed as a prop, default 2025)
function SkattemeldingPage({ year = 2025, setRoute, setYear }) {
  const TAX_STEPS = taxSteps();
  const TAX_RAIL = taxRail();
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const taxModel = React.useMemo(() => getTaxReturnPageModel(year), [backend?.appState, backend?.activeBusinessId, year]);
  const annualModel = React.useMemo(() => getAnnualAccountsPageModel(year), [backend?.appState, backend?.activeBusinessId, year]);
  const company = taxModel.company || {};
  const modelContact = taxModel.contact || emptyTaxReturnModel(year).contact;
  const submission = taxModel.submission || {};
  const summary = taxModel.summary || {};
  const wealth = taxModel.wealth || {};
  const taxBasis = taxModel.taxBasis || emptyTaxReturnModel(year).taxBasis;
  const taxAssets = taxModel.taxAssets || emptyTaxReturnModel(year).taxAssets;
  const taxOther = taxModel.taxOther || emptyTaxReturnModel(year).taxOther;
  const taxIncomeBalance = taxModel.taxIncomeBalance || emptyTaxReturnModel(year).taxIncomeBalance;
  const taxSettlementModel = taxModel.taxSettlement || {};
  const process = taxModel.process || {};
  const taxYearClosed = Boolean(taxModel.status?.yearClosed);
  const annualIncome = annualModel.incomeStatement || emptyAnnualAccountsModel().incomeStatement;
  const annualBalance = annualModel.balanceSheet || emptyAnnualAccountsModel().balanceSheet;
  const resultBeforeTax = Number(summary.resultBeforeTax || 0);
  const personalTaxFlow = taxModel.isEnk === true;
  const availablePermanentAdditions = taxPermanentPostsFor(permTillegg(), year, personalTaxFlow);
  const availablePermanentDeductions = taxPermanentPostsFor(permFradrag(), year, personalTaxFlow);
  const availableTemporaryDifferences = taxTemporaryPostsFor(year, personalTaxFlow);
  const taxRuleSupported = taxModel.reviewRequired !== true && summary.taxRate !== null && Number.isFinite(Number(summary.taxRate));
  const taxRate = taxRuleSupported ? Number(summary.taxRate) : 0;
  const stageFromProcess = { review: 0, checked: 1, ready: 2, processing: 3, done: 4 };
  const readiness = taxModel.readiness || { ready: false, blockers: [] };
  const [phase, setPhase] = useState(process.phase === 'draft' ? 'draft' : 'intro'); // intro | draft
  const [active, setActive] = useState(process.activeStepId || 'summary');
  const [done, setDone] = useState(Array.isArray(process.confirmedStepIds) ? process.confirmedStepIds : []);
  const [stage, setStage] = useState(stageFromProcess[process.submissionStage] ?? 0);
  const [allDetails, setAllDetails] = useState(null);
  useTaxReturnPageControlAdapter({ year, model: taxModel, phase, setPhase, active, setActive, done, steps: TAX_STEPS, summary: { resultBeforeTax, taxableIncome: summary.taxableBase ?? null, estimatedTax: taxModel.reviewRequired === true ? null : summary.taxAmount ?? null } });
  // editable summary data
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState(modelContact);
  const [contactDraft, setContactDraft] = useState(null);
  const [period, setPeriod] = useState(submission.period || `01.01-31.12.${year}`);
  const [ruleType, setRuleType] = useState(submission.accountingRuleType || 'Regnskapslovens regler for små foretak');
  const [auditor, setAuditor] = useState(submission.auditor || 'Nei');
  const editingContact = contactDraft !== null;
  React.useEffect(() => {
    if (!editingContact) setContact(modelContact);
  }, [editingContact, modelContact.navn, modelContact.tlf, modelContact.epost, modelContact.mobil]);
  React.useEffect(() => {
    setPeriod(submission.period || `01.01-31.12.${year}`);
    setRuleType(submission.accountingRuleType || 'Regnskapslovens regler for små foretak');
    setAuditor(submission.auditor || 'Nei');
  }, [year, submission.period, submission.accountingRuleType, submission.auditor]);
  React.useEffect(() => {
    setPhase(process.phase === 'draft' ? 'draft' : 'intro');
    setActive(process.activeStepId || 'summary');
    setDone(Array.isArray(process.confirmedStepIds) ? process.confirmedStepIds : []);
    setStage(stageFromProcess[process.submissionStage] ?? 0);
    setAllDetails(null);
    setContactDraft(null);
  }, [backend?.appState, year]);
  const startContactEdit = () => { setContactDraft({ ...contact }); setContactOpen(true); };
  const saveContact = () => {
    setContact(contactDraft);
    setContactDraft(null);
    saveTaxSubmissionPatch({ submitter: contactDraft?.navn || contact.navn });
    toast(t('clo.contactInfoSaved'), 'success');
  };
  const cancelContact = () => setContactDraft(null);
  const contactSummary = `${contact.navn}, ${contact.mobil}, ${contact.epost}`;
  // step 2 — Skattegrunnlag: permanent + temporary differences drive the calculation
  const [openSpec, setOpenSpec] = useState('perm');
  const [openWealth, setOpenWealth] = useState('assets');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [midlPicker, setMidlPicker] = useState(false);
  const [specMenu, setSpecMenu] = useState(false);
  const [permPosts, setPermPosts] = useState(Array.isArray(taxBasis.permanentDifferences) ? taxBasis.permanentDifferences : []);
  const automaticPermPosts = Array.isArray(taxBasis.automaticPermanentDifferences) ? taxBasis.automaticPermanentDifferences : [];
  const [midlPosts, setMidlPosts] = useState(Array.isArray(taxBasis.temporaryDifferences) ? taxBasis.temporaryDifferences : []);
  const automaticMidlPosts = Array.isArray(taxBasis.automaticTemporaryDifferences) ? taxBasis.automaticTemporaryDifferences : [];
  const [permDraft, setPermDraft] = useState(null);
  const [midlDraft, setMidlDraft] = useState(null);
  const [dtaOverride, setDtaOverride] = useState(taxBasis.deferredTaxAssetOverride ?? null);
  const [usk, setUsk] = useState(taxBasis.lossCarryforward || { fremfortTidl: 0, underhand: null, restUnderhand: null, aaretsAnv: 0 });
  const [extraSpecs, setExtraSpecs] = useState(Array.isArray(taxBasis.extraSpecs) ? taxBasis.extraSpecs : []);
  const [extraSpecValues, setExtraSpecValues] = useState(taxBasis.extraSpecValues || {});
  const [assetItems, setAssetItems] = useState(Array.isArray(taxAssets.items) ? taxAssets.items : []);
  const [assetDraft, setAssetDraft] = useState(null);
  const [ownerBenefits, setOwnerBenefits] = useState(taxOther.ownerBenefits || {});
  const [companyOtherInfo, setCompanyOtherInfo] = useState(taxOther.companyInfo || {});
  const [soleTraderInfo, setSoleTraderInfo] = useState(taxOther.soleTrader || {});
  const [otherInvestments, setOtherInvestments] = useState(Array.isArray(taxOther.investments) ? taxOther.investments : []);
  const [taxSettlement, setTaxSettlement] = useState(taxSettlementModel);
  const [otherEdit, setOtherEdit] = useState(null);
  const [otherDraft, setOtherDraft] = useState(null);
  const [ibPosts, setIbPosts] = useState(Array.isArray(taxIncomeBalance.posts) ? taxIncomeBalance.posts : []);
  const [ibSpecs, setIbSpecs] = useState(Array.isArray(taxIncomeBalance.specs) ? taxIncomeBalance.specs : []);
  const [ibEquityChanges, setIbEquityChanges] = useState(Array.isArray(taxIncomeBalance.equityChanges) ? taxIncomeBalance.equityChanges : []);
  const [ibPrincipleChanges, setIbPrincipleChanges] = useState(Array.isArray(taxIncomeBalance.principleChanges) ? taxIncomeBalance.principleChanges : []);
  const [ibOpen, setIbOpen] = useState('inventory');
  const [ibPostDraft, setIbPostDraft] = useState(null);
  const [ibSpecDraft, setIbSpecDraft] = useState(null);
  const [ibEquityDraft, setIbEquityDraft] = useState(null);
  const [ibPrincipleDraft, setIbPrincipleDraft] = useState(null);
  React.useEffect(() => {
    const nextBasis = taxModel.taxBasis || emptyTaxReturnModel(year).taxBasis;
    setPermPosts(Array.isArray(nextBasis.permanentDifferences) ? nextBasis.permanentDifferences : []);
    setMidlPosts(Array.isArray(nextBasis.temporaryDifferences) ? nextBasis.temporaryDifferences : []);
    setDtaOverride(nextBasis.deferredTaxAssetOverride ?? null);
    setUsk(nextBasis.lossCarryforward || { fremfortTidl: 0, underhand: null, restUnderhand: null, aaretsAnv: 0 });
    setExtraSpecs(Array.isArray(nextBasis.extraSpecs) ? nextBasis.extraSpecs : []);
    setExtraSpecValues(nextBasis.extraSpecValues || {});
    const nextAssets = taxModel.taxAssets || emptyTaxReturnModel(year).taxAssets;
    setAssetItems(Array.isArray(nextAssets.items) ? nextAssets.items : []);
    const nextOther = taxModel.taxOther || emptyTaxReturnModel(year).taxOther;
    setOwnerBenefits(nextOther.ownerBenefits || {});
    setCompanyOtherInfo(nextOther.companyInfo || {});
    setSoleTraderInfo(nextOther.soleTrader || {});
    setOtherInvestments(Array.isArray(nextOther.investments) ? nextOther.investments : []);
    setTaxSettlement(taxModel.taxSettlement || {});
    const nextIb = taxModel.taxIncomeBalance || emptyTaxReturnModel(year).taxIncomeBalance;
    setIbPosts(Array.isArray(nextIb.posts) ? nextIb.posts : []);
    setIbSpecs(Array.isArray(nextIb.specs) ? nextIb.specs : []);
    setIbEquityChanges(Array.isArray(nextIb.equityChanges) ? nextIb.equityChanges : []);
    setIbPrincipleChanges(Array.isArray(nextIb.principleChanges) ? nextIb.principleChanges : []);
  }, [backend?.appState, year]);
  const saveTaxProgress = React.useCallback(async (patch) => {
    const save = window.PlummeBackend?.saveTaxReturnProgress;
    if (!save) return;
    try {
      await save({ year, patch });
    } catch (error) {
      toast(error?.message || t('clo.taxDraftSaveFailed'), 'error');
    }
  }, [year]);
  const saveTaxSubmissionPatch = (patch) => {
    void saveTaxProgress({ phase: 'draft', activeStepId: active, submission: patch });
  };
  const saveTaxBasis = React.useCallback(async (patch) => {
    const save = window.PlummeBackend?.saveTaxReturnProgress;
    if (!save) return;
    try {
      await save({ year, patch: { phase: 'draft', activeStepId: 'tax-base', taxBasis: patch } });
    } catch (error) {
      toast(error?.message || t('clo.taxBasisSaveFailed'), 'error');
    }
  }, [year]);
  const addExtraSpec = (title) => {
    setSpecMenu(false);
    const next = extraSpecs.includes(title) ? extraSpecs : [...extraSpecs, title];
    setExtraSpecs(next);
    setOpenSpec(title);
    void saveTaxBasis({ extraSpecs: next });
    toast(t('clo.itemAdded', { title }), 'success');
  };
  const calculatedPermPosts = [...permPosts, ...automaticPermPosts];
  const calculatedMidlPosts=[...midlPosts,...automaticMidlPosts];
  // The same calculation as the tax return model, with the backend's rate, applied to the unsaved page state.
  const taxCalc = window.PlummeTaxBasisCalculation.calculate({ permanentDifferences: calculatedPermPosts, temporaryDifferences: calculatedMidlPosts, lossCarryforward: usk, deferredTaxAssetOverride: dtaOverride }, resultBeforeTax, summary.taxRate);
  const { permanentDifference: permSum, temporaryDifferenceChange: midlEndring, temporaryDifference: midlForskjell, lossCarry, lossApplied, taxable, fremfortInntekt, sumDTA, ikkeBokfort, grunnlagDTA } = taxCalc;
  const fremforbart = fremfortInntekt;
  const taxAmount = taxRuleSupported ? taxCalc.taxAmount ?? 0 : 0;
  const utsattSkatt = taxRuleSupported ? taxCalc.utsattSkatt ?? 0 : 0;
  const onPickPerm = (post) => { setPickerOpen(false); setPermDraft({ id: null, code: post.code, label: post.label, kind: post.kind, amountInput: '', desc: '' }); };
  const openEditPerm = (p) => setPermDraft({ ...p, amountInput: String(p.amount) });
  const savePerm = () => {
    const amt = parseNor(permDraft.amountInput);
    const next = permDraft.id
      ? permPosts.map((p) => p.id === permDraft.id ? { ...permDraft, amount: amt } : p)
      : [...permPosts, { ...permDraft, id: 'pp' + Date.now(), amount: amt }];
    setPermPosts(next);
    setPermDraft(null);
    void saveTaxBasis({ permanentDifferences: next });
    toast(t('clo.permanentDiffSaved'), 'success');
  };
  const deletePerm = () => {
    const next = permPosts.filter((p) => p.id !== permDraft.id);
    setPermPosts(next);
    setPermDraft(null);
    void saveTaxBasis({ permanentDifferences: next });
    toast(t('clo.entryDeleted'), 'success');
  };
  const onPickMidl = (post) => { setMidlPicker(false); setMidlDraft({ id: null, code: post.code, label: post.label, rmAarI: '', smAarI: '', rmFjorI: '', smFjorI: '', desc: '' }); };
  const openEditMidl = (p) => setMidlDraft({ id: p.id, code: p.code, label: p.label, rmAarI: String(p.rmAar), smAarI: String(p.smAar), rmFjorI: String(p.rmFjor), smFjorI: String(p.smFjor), desc: p.desc || '' });
  const saveMidl = () => {
    const np = { id: midlDraft.id || 'mp' + Date.now(), code: midlDraft.code, label: midlDraft.label, rmAar: parseNor(midlDraft.rmAarI), smAar: parseNor(midlDraft.smAarI), rmFjor: parseNor(midlDraft.rmFjorI), smFjor: parseNor(midlDraft.smFjorI), desc: midlDraft.desc };
    const next = midlDraft.id ? midlPosts.map((p) => p.id === midlDraft.id ? np : p) : [...midlPosts, np];
    setMidlPosts(next);
    setMidlDraft(null);
    void saveTaxBasis({ temporaryDifferences: next });
    toast(t('clo.temporaryDiffSaved'), 'success');
  };
  const deleteMidl = () => {
    const next = midlPosts.filter((p) => p.id !== midlDraft.id);
    setMidlPosts(next);
    setMidlDraft(null);
    void saveTaxBasis({ temporaryDifferences: next });
    toast(t('clo.entryDeleted'), 'success');
  };
  const saveDtaOverride = (value) => {
    setDtaOverride(value);
    void saveTaxBasis({ deferredTaxAssetOverride: value });
  };
  const saveUskPatch = (patch) => {
    const next = { ...usk, ...patch };
    setUsk(next);
    void saveTaxBasis({ lossCarryforward: next });
  };
  const removeExtraSpec = (title) => {
    const next = extraSpecs.filter((entry) => entry !== title);
    const nextValues = { ...extraSpecValues };
    delete nextValues[title];
    setExtraSpecs(next);
    setExtraSpecValues(nextValues);
    void saveTaxBasis({ extraSpecs: next, extraSpecValues: nextValues });
    toast(t('clo.specificationRemoved'), 'success');
  };
  const saveExtraSpecValue = (title, value) => {
    const next = { ...extraSpecValues, [title]: value };
    setExtraSpecValues(next);
    void saveTaxBasis({ extraSpecValues: next });
  };
  const saveTaxAssets = React.useCallback(async (items) => {
    const save = window.PlummeBackend?.saveTaxReturnProgress;
    if (!save) return;
    try {
      const manualItems = items.filter((entry) => entry.source !== 'fixed-assets');
      await save({ year, patch: { phase: 'draft', activeStepId: 'assets', taxAssets: { items: manualItems } } });
    } catch (error) {
      toast(error?.message || t('clo.taxAssetSaveFailed'), 'error');
    }
  }, [year]);
  const openNewTaxAsset = (type) => {
    const objectId = assetItems.filter((entry) => entry.type === type).length + 1;
    const values = type === 'saldo'
      ? { grp: 'A', sats: 30, fysisk: 'Ja' }
      : type === 'gtk'
        ? { sats: 100 }
        : {};
    setAssetDraft({ id: null, type, objectId, source: 'manual', values });
  };
  const openTaxAsset = (item) => {
    if (item.source === 'fixed-assets') return;
    setAssetDraft({ ...item, values: { ...(item.values || {}) } });
  };
  const saveTaxAssetDraft = () => {
    if (!assetDraft) return;
    const nextEntry = {
      ...assetDraft,
      id: assetDraft.id || `tax-asset-${Date.now()}`,
      source: 'manual',
      objectId: assetDraft.objectId || assetItems.filter((entry) => entry.type === assetDraft.type).length + 1,
      values: assetDraft.values || {},
    };
    const next = assetDraft.id
      ? assetItems.map((entry) => entry.id === assetDraft.id ? nextEntry : entry)
      : [...assetItems, nextEntry];
    setAssetItems(next);
    setAssetDraft(null);
    void saveTaxAssets(next);
    toast(t('clo.assetSaved', { title: taxAssetSchemas()[nextEntry.type]?.title || t('clo.assetFallback') }), 'success');
  };
  const deleteTaxAssetDraft = () => {
    if (!assetDraft?.id) return;
    const next = assetItems.filter((entry) => entry.id !== assetDraft.id);
    setAssetItems(next);
    setAssetDraft(null);
    void saveTaxAssets(next);
    toast(t('clo.objectDeleted'), 'success');
  };
  const taxAssetGroups = {
    saldo: assetItems.filter((entry) => entry.type === 'saldo'),
    linear: assetItems.filter((entry) => entry.type === 'linear'),
    none: assetItems.filter((entry) => entry.type === 'none'),
    gtk: assetItems.filter((entry) => entry.type === 'gtk'),
  };
  const saveTaxOther = React.useCallback(async (patch) => {
    const save = window.PlummeBackend?.saveTaxReturnProgress;
    if (!save) return;
    try {
      await save({ year, patch: { phase: 'draft', activeStepId: 'other', taxOther: patch } });
    } catch (error) {
      toast(error?.message || t('clo.taxOtherSaveFailed'), 'error');
    }
  }, [year]);
  const ownerBenefitRows = [
    { key: 'dividendPaid', label: t('clo.dividendPaidInYear', { year }), type: 'num' },
    { key: 'companyToOwnerBenefit', label: t('clo.companyToOwnerBenefit'), type: 'choice' },
    { key: 'ownerToCompanyBenefit', label: t('clo.ownerToCompanyBenefit'), type: 'choice' },
    { key: 'loansToOwnerAmount', label: t('clo.loansToOwnerAmount'), type: 'num' },
    { key: 'loansFromOwnerAmount', label: t('clo.loansFromOwnerAmount'), type: 'num' },
    { key: 'securityAmount', label: t('clo.securityAmount'), type: 'num' },
    { key: 'notes', label: t('clo.noteField'), type: 'textarea' },
  ];
  const companyOtherRows = [
    { key: 'industryRegime', label: t('clo.industryRegime'), options: [{ value: 'ordinary', label: t('clo.industryRegimeOrdinary') }, { value: 'agriculture_forestry', label: t('clo.industryRegimeAgricultureForestry') }, { value: 'regulated_finance', label: t('clo.industryRegimeRegulatedFinance') }, { value: 'other_special', label: t('clo.industryRegimeOtherSpecial') }] },
    { key: 'financeTaxApplicability', label: t('clo.financeTaxApplicability'), options: [{ value: 'not_applicable', label: t('clo.financeTaxNotApplicable') }, { value: 'applicable', label: t('clo.financeTaxApplicable') }, { value: 'unknown', label: t('clo.financeTaxUnknown') }] },
    { key: 'foreignOwnershipOrCrossBorderReorg', label: t('clo.foreignOwnership'), type: 'choice' },
    { key: 'wasGroupSubsidiary', label: t('clo.wasSubsidiary'), type: 'choice' },
    { key: 'taxFreeTransferOfAssetsOrLiabilities', label: t('clo.taxFreeTransfers'), type: 'choice' },
    { key: 'ownsProperty', label: t('clo.ownsProperty'), type: 'choice' },
    { key: 'propertyTaxValue', label: t('clo.propertyTaxValue'), type: 'num' },
    { key: 'propertyDetails', label: t('clo.propertyDetails'), type: 'textarea' },
    { key: 'groupReceivablesAmount', label: t('clo.groupReceivablesAmount'), type: 'num' },
    { key: 'groupDebtAmount', label: t('clo.groupDebtAmount'), type: 'num' },
    { key: 'groupDetails', label: t('clo.groupDetails'), type: 'textarea' },
    { key: 'notes', label: t('clo.noteField'), type: 'textarea' },
  ];
  const soleTraderRows = [
    { key: 'businessIncomeReviewed', label: t('clo.businessIncomeReviewed'), type: 'confirmation' },
    { key: 'otherPersonalIncomeReviewed', label: t('clo.otherPersonalIncomeReviewed'), type: 'confirmation' },
    { key: 'personalReturnReviewed', label: t('clo.personalReturnReviewed'), type: 'confirmation' },
    { key: 'duplicateIncomeChecked', label: t('clo.duplicateIncomeChecked'), type: 'confirmation' },
    { key: 'spouseAllocationStatus', label: t('clo.spouseAllocationStatus'), options: [{ value: 'not_applicable', label: t('clo.spouseAllocationNotApplicable') }, { value: 'reviewed', label: t('clo.spouseAllocationReviewed') }, { value: 'requires_review', label: t('clo.spouseAllocationRequiresReview') }] },
    { key: 'spouseShareAmount', label: t('clo.spouseShareAmount'), type: 'num' },
    { key: 'spouseAllocationBasis', label: t('clo.spouseAllocationBasis'), type: 'textarea' },
    { key: 'notes', label: t('clo.noteField'), type: 'textarea' },
  ];
  const taxSettlementRows = [
    { key: 'advanceTaxPaid', label: t('clo.advanceTaxPaid'), type: 'num' },
    { key: 'additionalAdvanceTax', label: t('clo.additionalAdvanceTax'), type: 'num' },
    { key: 'status', label: t('clo.taxSettlementStatus'), options: [{ value: 'pending', label: t('clo.taxSettlementPending') }, { value: 'received', label: t('clo.taxSettlementReceived') }, { value: 'settled', label: t('clo.taxSettlementSettled') }] },
    { key: 'finalAssessedTax', label: t('clo.finalAssessedTax'), type: 'num' },
    { key: 'interestIncome', label: t('clo.interestIncome'), type: 'num' },
    { key: 'interestExpense', label: t('clo.interestExpense'), type: 'num' },
    { key: 'settlementDate', label: t('clo.settlementDate'), type: 'text' },
    { key: 'notes', label: t('clo.settlementNotesOrRef'), type: 'textarea' },
  ];
  const openNewOtherInvestment = (type) => setOtherDraft({ id: null, type, source: 'manual', values: {}, openSections: [] });
  const openOtherInvestment = (investment) => {
    if (investment.source === 'trial-balance') return;
    setOtherDraft({ ...investment, values: { ...(investment.values || {}) }, openSections: [...(investment.openSections || [])] });
  };
  const saveOwnerBenefits = () => {
    setOwnerBenefits(otherEdit.draft);
    setOtherEdit(null);
    void saveTaxOther({ ownerBenefits: otherEdit.draft });
    toast(t('clo.ownerBenefitsSaved'), 'success');
  };
  const saveCompanyOtherInfo = () => {
    setCompanyOtherInfo(otherEdit.draft);
    setOtherEdit(null);
    void saveTaxOther({ companyInfo: otherEdit.draft });
    toast(t('clo.companyInfoSaved'), 'success');
  };
  const saveSoleTraderInfo = () => {
    setSoleTraderInfo(otherEdit.draft);
    setOtherEdit(null);
    void saveTaxOther({ soleTrader: otherEdit.draft });
    toast(t('clo.soleTraderChecksSaved'), 'success');
  };
  const saveTaxSettlement = () => {
    setTaxSettlement(otherEdit.draft);
    setOtherEdit(null);
    void saveTaxProgress({ phase: 'draft', activeStepId: active, taxSettlement: otherEdit.draft });
    toast(t('clo.taxSettlementSaved'), 'success');
  };
  const saveOtherInvestmentDraft = () => {
    if (!otherDraft) return;
    const nextEntry = {
      ...otherDraft,
      id: otherDraft.id || `tax-other-${Date.now()}`,
      source: 'manual',
      values: otherDraft.values || {},
      openSections: otherDraft.openSections || [],
    };
    const next = otherDraft.id
      ? otherInvestments.map((entry) => entry.id === otherDraft.id ? nextEntry : entry)
      : [...otherInvestments, nextEntry];
    setOtherInvestments(next);
    setOtherDraft(null);
    void saveTaxOther({ investments: next.filter((entry) => entry.source !== 'trial-balance') });
    toast(t('clo.investmentSaved', { title: otherInvestmentSchemas()[nextEntry.type]?.tableTitle || t('clo.investmentFallback') }), 'success');
  };
  const deleteOtherInvestmentDraft = () => {
    if (!otherDraft?.id) return;
    const next = otherInvestments.filter((entry) => entry.id !== otherDraft.id);
    setOtherInvestments(next);
    setOtherDraft(null);
    void saveTaxOther({ investments: next.filter((entry) => entry.source !== 'trial-balance') });
    toast(t('clo.investmentDeleted'), 'success');
  };
  const saveTaxIncomeBalance = React.useCallback(async (patch) => {
    const save = window.PlummeBackend?.saveTaxReturnProgress;
    if (!save) return;
    try {
      await save({ year, patch: { phase: 'draft', activeStepId: 'income-balance', taxIncomeBalance: patch } });
    } catch (error) {
      toast(error?.message || t('clo.incomeBalanceSaveFailed'), 'error');
    }
  }, [year]);
  const openNewIbPost = (account) => setIbPostDraft({ id: null, kind: account.kind, code: account.code, label: account.label, baseAmount: null, amount: null, wealthValue: account.kind === 'balance' ? 0 : null, description: '' });
  const saveIbPostDraft = () => {
    if (!ibPostDraft?.code || !ibPostDraft?.label) {
      toast(t('clo.enterAccountBeforeSaving'), 'error');
      return;
    }
    const nextEntry = {
      ...ibPostDraft,
      id: ibPostDraft.id || `tax-income-balance-post-${Date.now()}`,
      kind: ibPostDraft.kind || taxIbManualPostKind(ibPostDraft.code),
      amount: taxIbEffectiveValue(ibPostDraft.amountEdit || { base: ibPostDraft.amount, mode: 'none' }),
      wealthValue: ibPostDraft.kind === 'balance' ? taxIbEffectiveValue(ibPostDraft.wealthEdit || { base: ibPostDraft.wealthValue, mode: 'none' }) : null,
    };
    const next = ibPostDraft.id ? ibPosts.map((entry) => entry.id === ibPostDraft.id ? nextEntry : entry) : [...ibPosts, nextEntry];
    setIbPosts(next);
    setIbPostDraft(null);
    void saveTaxIncomeBalance({ posts: next });
    toast(t('clo.entrySaved'), 'success');
  };
  const deleteIbPostDraft = () => {
    if (!ibPostDraft?.id) return;
    const next = ibPosts.filter((entry) => entry.id !== ibPostDraft.id);
    setIbPosts(next);
    setIbPostDraft(null);
    void saveTaxIncomeBalance({ posts: next });
    toast(t('clo.entryDeletedShort'), 'success');
  };
  const openNewIbSpec = ([type, title]) => {
    const existing = taxIbSpecByType(ibSpecs, type);
    const defaults = type === 'accommodation' && company.orgnr ? { orgnr: company.orgnr } : {};
    setIbSpecDraft({ ...(existing || { id: null, type, title, values: defaults }), title, values: { ...defaults, ...(existing?.values || {}) } });
  };
  const saveIbSpecDraft = () => {
    if (!ibSpecDraft?.type || !ibSpecDraft?.title) return;
    const nextEntry = { ...ibSpecDraft, id: ibSpecDraft.id || `tax-income-balance-spec-${Date.now()}`, values: ibSpecDraft.values || {} };
    const existing = taxIbSpecByType(ibSpecs, ibSpecDraft.type);
    const next = existing ? ibSpecs.map((entry) => entry.id === existing.id ? { ...nextEntry, id: existing.id } : entry) : [...ibSpecs, nextEntry];
    setIbSpecs(next);
    setIbSpecDraft(null);
    void saveTaxIncomeBalance({ specs: next });
    toast(t('clo.specificationSaved'), 'success');
  };
  const deleteIbSpecDraft = () => {
    if (!ibSpecDraft?.id) return;
    if (ibSpecDraft.type === 'payroll') {
      const preservedValues = Object.fromEntries(['naturalytelser', 'pensjon'].filter((key) => ibSpecDraft.values?.[key] != null).map((key) => [key, ibSpecDraft.values[key]]));
      if (Object.keys(preservedValues).length) {
        const next = ibSpecs.map((entry) => entry.id === ibSpecDraft.id ? { ...entry, values: preservedValues } : entry);
        setIbSpecs(next);
        setIbSpecDraft(null);
        void saveTaxIncomeBalance({ specs: next });
        toast(t('clo.detailedPayrollSpecDeleted'), 'success');
        return;
      }
    }
    const next = ibSpecs.filter((entry) => entry.id !== ibSpecDraft.id);
    setIbSpecs(next);
    setIbSpecDraft(null);
    void saveTaxIncomeBalance({ specs: next });
    toast(t('clo.specificationDeleted'), 'success');
  };
  const saveIbSpecs = (next) => {
    setIbSpecs(next);
    void saveTaxIncomeBalance({ specs: next });
  };
  const addIbEquityChange = (item) => {
    const nextEntry = { id: `tax-equity-change-${Date.now()}`, type: item.label, sign: item.sign, amount: 0, description: '' };
    const next = [...ibEquityChanges, nextEntry];
    setIbEquityChanges(next);
    void saveTaxIncomeBalance({ equityChanges: next });
    toast(t('clo.itemAdded', { title: item.label }), 'success');
  };
  const saveIbEquityDraft = () => {
    if (!ibEquityDraft?.type) return;
    const nextEntry = {
      ...ibEquityDraft,
      id: ibEquityDraft.id || `tax-equity-change-${Date.now()}`,
      amount: ibEquityDraft.amount == null ? null : Number(ibEquityDraft.amount),
    };
    const next = ibEquityDraft.id ? ibEquityChanges.map((entry) => entry.id === ibEquityDraft.id ? nextEntry : entry) : [...ibEquityChanges, nextEntry];
    setIbEquityChanges(next);
    setIbEquityDraft(null);
    void saveTaxIncomeBalance({ equityChanges: next });
    toast(t('clo.equityChangeSaved'), 'success');
  };
  const deleteIbEquityDraft = () => {
    if (!ibEquityDraft?.id) return;
    const next = ibEquityChanges.filter((entry) => entry.id !== ibEquityDraft.id);
    setIbEquityChanges(next);
    setIbEquityDraft(null);
    void saveTaxIncomeBalance({ equityChanges: next });
    toast(t('clo.equityChangeDeleted'), 'success');
  };
  const saveIbPrincipleDraft = () => {
    if (!ibPrincipleDraft?.code || !ibPrincipleDraft?.label) return;
    const nextEntry = {
      ...ibPrincipleDraft,
      id: ibPrincipleDraft.id || `tax-principle-change-${Date.now()}`,
      positive: ibPrincipleDraft.positive == null ? null : Number(ibPrincipleDraft.positive),
      negative: ibPrincipleDraft.negative == null ? null : Number(ibPrincipleDraft.negative),
    };
    const next = ibPrincipleDraft.id ? ibPrincipleChanges.map((entry) => entry.id === ibPrincipleDraft.id ? nextEntry : entry) : [...ibPrincipleChanges, nextEntry];
    setIbPrincipleChanges(next);
    setIbPrincipleDraft(null);
    void saveTaxIncomeBalance({ principleChanges: next });
    toast(t('clo.principleChangeSaved'), 'success');
  };
  const deleteIbPrincipleDraft = () => {
    if (!ibPrincipleDraft?.id) return;
    const next = ibPrincipleChanges.filter((entry) => entry.id !== ibPrincipleDraft.id);
    setIbPrincipleChanges(next);
    setIbPrincipleDraft(null);
    void saveTaxIncomeBalance({ principleChanges: next });
    toast(t('clo.principleChangeDeleted'), 'success');
  };
  const ibAccountOptions = React.useMemo(() => taxIbAccountOptions(annualIncome, annualBalance), [annualIncome, annualBalance]);
  const ibInventorySpec = taxIbSpecByType(ibSpecs, 'inventory');
  const ibReceivablesSpec = taxIbSpecByType(ibSpecs, 'receivables');
  const ibPayrollSpec = taxIbSpecByType(ibSpecs, 'payroll');
  const ibExtraSpecs = ibSpecs.filter((spec) => !TAX_IB_BUILT_IN_SPEC_TYPES.includes(spec.type));
  const ibInventoryRows = (annualBalance.currentAssetGroups || []).find((group) => group.key === 'inventory')?.rows || [];
  const ibReceivableRows = (annualBalance.currentAssetGroups || []).find((group) => group.key === 'receivables')?.rows || [];
  const ibOtherReceivableRows = [
    ...(annualBalance.fixedAssetGroups || []).flatMap((group) => group.rows || []),
    ...(annualBalance.currentAssetGroups || []).flatMap((group) => group.rows || []),
  ].filter((row) => /^(13|16)/.test(String(row.code || '')));
  const saveIbSpecValues = (type, title, values) => saveIbSpecs(taxIbSetSpec(ibSpecs, type, title, values));
  const taxBalanceOk = Math.abs(Number(annualBalance.difference || 0)) <= 0.5;
  const allStatusClass = (status) => [t('clo.statusDiscrepancy'), t('clo.statusMissingInfo')].includes(status) ? 'todo' : 'done';
  const renderTaxDetailRows = (rows) => <div className="cl-detail">{rows.map(([label, value]) => <DetailRow key={`${label}-${value}`} label={label} value={value} />)}</div>;
  const renderTaxAccountRows = (rows, emptyText = t('clo.noBalanceOnRelevantAccounts')) =>
    Array.isArray(rows) && rows.length ? rows.map((row) => <Line key={`${row.code || row.label}-${row.amount}`} code={row.code} label={row.label} value={fmt(row.amount || 0)} />) : <p className="muted">{emptyText}</p>;
  const taxAllSummaryCards = [
    {
      id: 'submission',
      title: t('clo.submissionAndCompany'),
      status: [submission.submitter || contact.navn, period, ruleType].every(Boolean) ? t('clo.statusReady') : t('clo.statusMissingInfo'),
      rows: [[t('clo.company'), company.name || '-'], [t('clo.orgNumber'), company.orgnr || '-'], [t('clo.incomeYear'), year], [t('clo.accountingPeriod'), period || '-']],
    },
    {
      id: 'tax-base',
      title: t('clo.taxBase'),
      status: t('clo.statusReady'),
      rows: [[t('clo.profitBeforeTax'), fmt(resultBeforeTax)], [t('clo.permanentDiffs'), fmt(permSum)], [t('clo.temporaryDiffs'), fmt(midlEndring)], [t('clo.taxPayable'), fmt(taxAmount)]],
    },
    {
      id: 'wealth',
      title: t('clo.wealthAndDebt'),
      status: wealth.valuation?.ready ? t('clo.statusReady') : t('clo.statusControl'),
      rows: [[t('clo.wealth.total'), fmt(wealth.totalAssets || 0)], [t('clo.totalDebt'), fmt(wealth.totalDebt || 0)], [t('clo.netWealthLabel'), fmt(wealth.netWealth || 0)], [t('clo.reportedWealth'), wealth.valuation?.ready ? fmt(wealth.valuation.reportedNetWealth) : t('clo.statusControl')]],
    },
    {
      id: 'assets',
      title: t('clo.taxAssetsCard'),
      status: assetItems.length ? t('clo.objectsCount', { count: assetItems.length }) : t('clo.statusNoItems'),
      rows: [[t('clo.savingsDepreciated'), taxAssetGroups.saldo.length], [t('clo.linearDepreciated'), taxAssetGroups.linear.length], [t('clo.notDepreciable'), taxAssetGroups.none.length], [t('clo.gainLossAccount'), taxAssetGroups.gtk.length]],
    },
    {
      id: 'other',
      title: t('clo.otherInfoCard'),
      status: otherInvestments.length ? t('clo.investmentsCount', { count: otherInvestments.length }) : t('clo.statusReady'),
      rows: [[t('clo.dividend'), otherFmt('num', ownerBenefits.dividendPaid)], [t('clo.companyToOwnerShort'), ownerBenefits.companyToOwnerBenefit || t('common.no')], [t('clo.ownerToCompanyShort'), ownerBenefits.ownerToCompanyBenefit || t('common.no')], [t('clo.investmentsShort'), otherInvestments.length]],
    },
    {
      id: 'income-balance',
      title: t('clo.incomeBalanceCard'),
      status: taxBalanceOk ? t('clo.statusReady') : t('clo.statusDiscrepancy'),
      rows: [[t('clo.operatingIncomeShort'), fmt(annualIncome.operatingIncome || 0)], [t('clo.annualResultShort'), fmt(annualIncome.annualResult ?? annualIncome.resultBeforeTax ?? resultBeforeTax)], [t('clo.totalAssetsShort'), fmt(annualBalance.totalAssets || 0)], [t('clo.balanceDeviation'), fmt(annualBalance.difference || 0)]],
    },
  ];
  const taxAllCardById = (id) => taxAllSummaryCards.find((card) => card.id === id);
  const renderTaxAllDetails = () => {
    if (!allDetails) return null;
    const card = taxAllCardById(allDetails);
    if (!card) return null;
    if (allDetails === 'tax-base') return <div className="cl-all-modal">
      {renderTaxDetailRows(card.rows)}
      <div className="cl-subhead">{t('clo.calculation')}</div>
      <Line label={t('clo.taxBase')} value={fmt(taxable)} strong />
      <Line label={t('clo.lossCarryforward')} value={fmt(fremforbart)} />
      <Line label={t('clo.deferredTaxOrAsset')} value={fmt(utsattSkatt)} />
      <div className="cl-subhead">{t('clo.permanentDiffs')}</div>
      {permPosts.length ? permPosts.map((post) => <Line key={post.id} code={post.code} label={post.label} value={fmt(post.amount || 0)} />) : <p className="muted">{t('clo.noPermanentDiffs')}</p>}
      <div className="cl-subhead">{t('clo.temporaryDiffs')}</div>
      {calculatedMidlPosts.length ? calculatedMidlPosts.map((post) => <Line key={post.id} code={post.code} label={post.label} value={fmt((post.rmAar || 0) - (post.smAar || 0))} />) : <p className="muted">{t('clo.noTemporaryDiffs')}</p>}
      {extraSpecs.length ? <><div className="cl-subhead">{t('clo.additionalSpecs')}</div>{extraSpecs.map((title) => <Line key={title} label={title} value={t('clo.added')} />)}</> : null}
    </div>;
    if (allDetails === 'wealth') return <div className="cl-all-modal"><TaxWealthBasisNotice wealth={wealth} />
      {renderTaxDetailRows(card.rows)}
      <div className="cl-subhead">{t('clo.assetsAndInventory')}</div>
      <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.assetInventoryRows || []} sum={wealth.assetsInventoryTotal || 0} />
      <div className="cl-subhead">{t('clo.sharesAndInvestments')}</div>
      <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.sharesInvestmentRows || []} sum={wealth.sharesInvestmentTotal || 0} empty={t('clo.noSharesOrInvestmentsInTrialBalance')} />
      <div className="cl-subhead">{t('clo.otherWealth')}</div>
      <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.otherAssetRows || []} sum={wealth.otherAssetsTotal || 0} />
      <div className="cl-subhead">{t('clo.debt')}</div>
      <WealthTable labelHead={t('clo.debtRelatedTo')} valueHead={t('clo.valueColumn')} rows={wealth.debtRows || []} sum={wealth.totalDebt || 0} empty={t('clo.noDebtItemsInTrialBalance')} />
    </div>;
    if (allDetails === 'assets') return <div className="cl-all-modal">
      {renderTaxDetailRows(card.rows)}
      {assetItems.length ? assetItems.map((item) => {
        const schema = taxAssetSchemas()[item.type] || taxAssetSchemas().saldo;
        const computed = (item.source === 'fixed-assets' ? item.values : taxAssetCompute(item.type, item.values || {}));
        const label = item.values?.beskrivelse || item.values?.grp || schema.title;
        return <div key={item.id} className="cl-all-note">
          <h4>{schema.title}</h4>
          <p>{label}</p>
          <Line label={t('clo.objectIdentifier')} value={item.objectId || '-'} />
          <Line label={t('clo.basis')} value={fmt(computed.grunnlag || 0)} />
          <Line label={t('clo.closingValue')} value={fmt(computed.utgaende || 0)} />
        </div>;
      }) : <p className="muted">{t('clo.noAssetsRegistered')}</p>}
    </div>;
    if (allDetails === 'other') return <div className="cl-all-modal">
      {renderTaxDetailRows(card.rows)}
      <div className="cl-subhead">{t('clo.otherCompanyInfo')}</div>
      {renderTaxDetailRows([
        [t('clo.foreignOwnershipShort'), companyOtherInfo.foreignOwnershipOrCrossBorderReorg || t('common.no')],
        [t('clo.subsidiaryInGroupShort'), companyOtherInfo.wasGroupSubsidiary || t('common.no')],
        [t('clo.taxFreeTransferShort'), companyOtherInfo.taxFreeTransferOfAssetsOrLiabilities || t('common.no')],
      ])}
      <div className="cl-subhead">{t('clo.investments')}</div>
      {otherInvestments.length ? otherInvestments.map((investment) => {
        const schema = otherInvestmentSchemas()[investment.type] || otherInvestmentSchemas().annet;
        return <Line key={investment.id} label={investment.values?.selskapnavn || investment.values?.navn || schema.tableTitle} value={otherFmt('num', investment.values?.formue)} />;
      }) : <p className="muted">{t('clo.noInvestments')}</p>}
    </div>;
    if (allDetails === 'income-balance') return <div className="cl-all-modal">
      {renderTaxDetailRows(card.rows)}
      <div className="cl-subhead">{t('clo.incomeStatement')}</div>
      <Line label={t('clo.operatingIncome')} value={fmt(annualIncome.operatingIncome || 0)} strong />
      {renderTaxAccountRows([...(annualIncome.salesIncomeRows || []), ...(annualIncome.otherOperatingIncomeRows || [])], t('clo.noIncomeAccounts'))}
      <Line label={t('clo.operatingCost')} value={fmt(annualIncome.operatingCosts || 0)} strong />
      {renderTaxAccountRows([...(annualIncome.productCostRows || []), ...(annualIncome.payrollCostRows || []), ...(annualIncome.depreciationRows || []), ...(annualIncome.otherOperatingCostRows || []), ...(annualIncome.financeCostRows || [])], t('clo.noCostAccounts'))}
      <div className="cl-subhead">{t('clo.balanceSheet')}</div>
      <Line label={t('clo.totalAssets')} value={fmt(annualBalance.totalAssets || 0)} strong />
      <Line label={t('clo.totalEquity')} value={fmt(annualBalance.totalEquity || 0)} />
      <Line label={t('clo.totalDebt')} value={fmt(annualBalance.totalDebt || 0)} />
      <Line label={t('clo.totalEquityAndDebt')} value={fmt(annualBalance.totalEquityAndDebt || 0)} strong />
      {(ibPosts.length || ibSpecs.length || ibEquityChanges.length || ibPrincipleChanges.length) ? <><div className="cl-subhead">{t('clo.manualAdditions')}</div>
        <Line label={t('clo.additionalItems')} value={ibPosts.length} />
        <Line label={t('clo.specifications')} value={ibSpecs.length} />
        <Line label={t('clo.equityChanges')} value={ibEquityChanges.length} />
        <Line label={t('clo.principleChanges')} value={ibPrincipleChanges.length} />
      </> : null}
    </div>;
    return renderTaxDetailRows(card.rows);
  };
  const idx = TAX_STEPS.findIndex((s) => s.id === active);
  const reviewDone = done.length === TAX_STEPS.length;
  const reopenTaxReturn = () => {
    setPhase('intro');
    setActive('summary');
    setDone([]);
    setStage(0);
    void saveTaxProgress(taxReturnReopenPatch());
    toast(t('clo.taxReturnReopened'), 'success');
  };

  const taxPreCloseNotice = !taxYearClosed
    ? <InfoBox title={t('clo.prepareTaxBeforeClosing')}>{t('clo.prepareTaxBeforeClosingBody')}</InfoBox>
    : null;

  const next = () => {
    const nd = done.includes(active) ? done : [...done, active];
    setDone(nd);
    const nextActive = idx < TAX_STEPS.length - 1 ? TAX_STEPS[idx + 1].id : active;
    if (idx < TAX_STEPS.length - 1) setActive(nextActive);
    const candidateReadiness = window.PlummeTaxReturnReadiness.readiness({
      state: backend?.appState || {}, year,
      process: { ...process, confirmedStepIds: nd, taxAssets: { items: assetItems }, taxBasis: { ...taxBasis, lossCarryforward: usk }, taxOther: { ownerBenefits, companyInfo: companyOtherInfo, soleTrader: soleTraderInfo }, taxSettlement },
      scope: taxModel.accountingScope,
    });
    const canAdvance = nd.length === TAX_STEPS.length && candidateReadiness.ready;
    if (active === 'all' && !candidateReadiness.ready) toast(t('clo.draftMissingControls', { count: candidateReadiness.blockers.length }), 'warning');
    else if (canAdvance && stage === 0) setStage(1);
    void saveTaxProgress({
      phase: 'draft',
      activeStepId: nextActive,
      confirmedStepIds: nd,
      taxBasis: { ...taxBasis, lossCarryforward: usk },
      taxOther: { ownerBenefits, companyInfo: companyOtherInfo, soleTrader: soleTraderInfo },
      taxSettlement,
      submissionStage: canAdvance && stage === 0 ? 'checked' : 'review',
    });
  };
  const back = () => {
    if (idx <= 0) return;
    const nextActive = TAX_STEPS[idx - 1].id;
    setActive(nextActive);
    void saveTaxProgress({ phase: 'draft', activeStepId: nextActive, confirmedStepIds: done });
  };

  if (phase === 'intro') {
    return <div>
      {window.ImportedComplianceNotice&&<window.ImportedComplianceNotice year={year} area="tax"/>}
      <Card className="cl-tax-intro">
        <PageHeader className="cl-page-head" title={t('clo.taxReturnTitle', { year })} subtitle={t('clo.taxReturnLead')} card={false}/>
        <div className="ph-sep"/>
        {taxPreCloseNotice}
        <div className={'cl-box cl-tax-info' + (!taxYearClosed ? ' mt-20' : '')} data-help-title={t('clo.aboutTaxReturn')} data-help={t('clo.aboutTaxReturnHelp')}>
          <h4><Icon name="info" size={17} />{t('clo.aboutTaxReturn')}</h4>
          <p>{t('clo.taxReturnIntro')}</p>
        </div>
        <div className="cl-tax-kpis">
          <div data-help-title={t('clo.incomeYear')} data-help={t('clo.incomeYearHelp')}><TaxIntroKpi tint="plum" icon="clock" label={t('clo.incomeYear')} value={year} sub={t('sharedText.clo.financialYear')} /></div>
          <div data-help-title={t('clo.calculatedTax')} data-help={t('clo.calculatedTaxHelp')}><TaxIntroKpi tint={taxRuleSupported && taxAmount > 0 ? 'red' : 'green'} icon="percent" label={personalTaxFlow ? t('clo.netBusinessIncome') : t('clo.calculatedTax')} value={personalTaxFlow ? fmt0(summary.netBusinessIncome || 0) : taxRuleSupported ? fmt0(taxAmount) : t('clo.mustBeClarified')} sub={personalTaxFlow ? t('clo.includedInPersonalTaxReturn') : taxRuleSupported ? (taxAmount > 0 ? t('clo.mustBePaid') : t('clo.noCalculatedTaxInDraft')) : t('clo.noRateApplied')} /></div>
          <div data-help-title={t('clo.lossCarryforward')} data-help={t('clo.lossCarryforwardHelp')}><TaxIntroKpi tint="peach" icon="trendDown" label={t('clo.lossCarryforward')} value={fmt0(fremfortInntekt)} sub={t('clo.toNextYear')} /></div>
        </div>
        <div className="cl-note-actions mt-20"><Button icon="arrowRight" onClick={() => { setPhase('draft'); void saveTaxProgress({ phase: 'draft', activeStepId: 'summary', confirmedStepIds: done }); }} data-help-title={t('clo.fetchAndCreateDraft')} data-help={t('clo.fetchAndCreateDraftHelp')}>{t('clo.fetchAndCreateDraft')}</Button></div>
      </Card>
    </div>;
  }

  return <div>
    {window.ImportedComplianceNotice&&<window.ImportedComplianceNotice year={year} area="tax"/>}
    {taxPreCloseNotice}
    <ClosingTaxReviewNotices model={taxModel} />{!personalTaxFlow && !taxRuleSupported && !taxModel.notCalculatedInputs?.length && <Card className="mb-16"><div className="cl-box cl-tax-info"><h4><Icon name="info" size={17} />{t('clo.taxCalcRequiresReview')}</h4><p>{t('clo.taxCalcRequiresReviewBody')}</p></div></Card>}
    {reviewDone && !readiness.ready ? <div className="alert alert-warning mb-16"><strong>{t('clo.taxReturnNotReadyYet')}</strong> {t('clo.mandatoryChecksMissing', { count: readiness.blockers.length })}</div> : null}
    <PageHeader className="cl-page-head" title={t('clo.taxReturnTitle', { year })} subtitle={reviewDone ? t('clo.taxReturnInternallyReviewed') : t('clo.draftForCompany', { company: company.name || t('clo.theBusinessFallback') })}/>
    <div className="cl-layout">
      <Card className="cl-main">
        <StepAccordion steps={TAX_STEPS} active={active} completed={done} onSelect={setActive}
          onBack={back} onNext={next} backDisabled={idx === 0}
          nextLabel={active === 'all' ? t('clo.reviewDone') : t('clo.continueLabel')}>
          {active === 'summary' && <>
            <CheckCard title={t('clo.checkContactInfo')} open={contactOpen} onToggle={setContactOpen} help={t('clo.checkContactInfoHelp')}>
              <p>{t('clo.contactInfoIntro')}</p>
              {editingContact ? <>
                <div className="cl-detail">
                  <EditField label={t('clo.name')} value={contactDraft.navn} onChange={(v) => setContactDraft((d) => ({ ...d, navn: v }))} />
                  <EditField label={t('clo.phone')} type="tel" value={contactDraft.tlf} onChange={(v) => setContactDraft((d) => ({ ...d, tlf: v }))} />
                  <EditField label={t('clo.email')} type="email" value={contactDraft.epost} onChange={(v) => setContactDraft((d) => ({ ...d, epost: v }))} />
                  <EditField label={t('clo.mobile')} type="tel" value={contactDraft.mobil} onChange={(v) => setContactDraft((d) => ({ ...d, mobil: v }))} />
                </div>
                <div className="cl-edit-actions"><Button size="sm" icon="check" onClick={saveContact}>{t('clo.save')}</Button><Button size="sm" variant="secondary" onClick={cancelContact}>{t('clo.cancel')}</Button></div>
              </> : <>
                <div className="cl-detail">
                  <DetailRow label={t('clo.name')} value={contact.navn} />
                  <DetailRow label={t('clo.phone')} value={contact.tlf} />
                  <DetailRow label={t('clo.email')} value={contact.epost} />
                  <DetailRow label={t('clo.mobile')} value={contact.mobil} />
                </div>
                <div><Button size="sm" variant="secondary" onClick={startContactEdit}>{t('clo.editContactInfo')}</Button></div>
              </>}
            </CheckCard>

            <CheckCard title={t('clo.hasAuditor')} help={t('clo.hasAuditorHelp')}>
              <p>{t('clo.hasAuditorIntro')}</p>
              <div className="cl-detail">
                <EditableDetailRow label={t('clo.auditorConfirms')} value={auditor} type="select" options={[{ value: 'Nei', label: t('clo.no') }, { value: 'Ja', label: t('sharedText.common.yes') }]} onSave={(value) => { setAuditor(value); saveTaxSubmissionPatch({ auditor: value }); }} />
              </div>
            </CheckCard>

            <div className="cl-box"><h4>{t('clo.filingDetails')}</h4>
              <div className="cl-detail">
                <DetailRow label={t('clo.incomeYear')} value={year} />
                <DetailRow label={t('clo.taxpayer')} value={submission.taxableLabel || company.name || t('clo.company')} />
                <DetailRow label={t('clo.filer')} value={submission.submitter || contact.navn} />
                <DetailRow label={t('clo.contactInfo')} value={contactSummary} onEdit={startContactEdit} />
                <EditableDetailRow label={t('clo.accountingPeriod')} value={period} onSave={(value) => { setPeriod(value); saveTaxSubmissionPatch({ period: value }); }} />
                <EditableDetailRow label={t('clo.annualAccountsRuleType')} value={ruleType} type="select" options={[{ value: 'Regnskapslovens regler for små foretak', label: t('clo.ruleType.smaForetak', 'Regnskapslovens regler for små foretak') }, { value: 'Regnskapslovens regler for øvrige foretak', label: t('clo.ruleType.ovrigeForetak', 'Regnskapslovens regler for øvrige foretak') }, { value: 'Forenklet IFRS', label: t('clo.ruleType.forenkletIfrs', 'Forenklet IFRS') }, { value: 'Full IFRS', label: t('clo.ruleType.fullIfrs', 'Full IFRS') }]} onSave={(value) => { setRuleType(value); saveTaxSubmissionPatch({ accountingRuleType: value }); }} help={t('clo.annualAccountsRuleTypeHelp')} />
                <EditableDetailRow label={t('clo.auditorApproval')} value={auditor} type="select" options={[{ value: 'Nei', label: t('clo.no') }, { value: 'Ja', label: t('sharedText.common.yes') }]} onSave={(value) => { setAuditor(value); saveTaxSubmissionPatch({ auditor: value }); }} />
              </div>
            </div>

            <div className="cl-subhead">{t('clo.taxCalculatedForYear', { year })}</div>
            <div>
              <Line label={personalTaxFlow ? t('clo.netBusinessIncome') : t('clo.taxBase')} value={fmt(personalTaxFlow ? summary.netBusinessIncome || 0 : taxable)} help={t('clo.taxBaseHelp')} />
              {!personalTaxFlow && <Line label={t('clo.taxRate')} value={taxRuleSupported ? Math.round(taxRate * 100) + ' %' : t('clo.mustBeClarified')} help={t('clo.taxRateHelp')} />}
              {!personalTaxFlow && <Line label={t('clo.taxPayable')} value={taxRuleSupported ? fmt(taxAmount) : t('clo.notCalculated')} strong help={t('clo.taxPayableHelp')} />}
            </div>

            <div className="cl-subhead">{t('clo.wealthAndDebt')}</div><TaxWealthBasisNotice wealth={wealth} />
            <div>
              <Line prefix="+" label={t('clo.wealth')} value={fmt(wealth.totalAssets || 0)} />
              <Line prefix="−" label={t('clo.debt')} value={fmt(wealth.totalDebt || 0)} />
              <Line prefix="=" label={t('clo.totalWealthAndDebt')} value={fmt(wealth.netWealth || 0)} strong help={t('clo.netWealthHelp')} />
            </div>
          </>}
          {active === 'tax-base' && <>
            <CheckCard title={t('clo.checkTempDiffsLastYear')} help={t('clo.checkTempDiffsHelp')}>
              <p>{t('clo.checkTempDiffsLastYearIntro')}</p>
              <div className="cl-detail">
                <DetailRow label={t('clo.tempDiffsAtOpening')} value={fmt(calculatedMidlPosts.reduce((sum, row) => sum + Number(row.rmFjor || 0) - Number(row.smFjor || 0), 0))} help={t('clo.tempDiffsAtOpeningHelp')} />
                <DetailRow label={t('clo.lossCarryforwardAtOpening')} value={fmt(usk.fremfortTidl || 0)} help={t('clo.lossCarryforwardAtOpeningHelp')} />
              </div>
            </CheckCard>

            <div>
              <h3 className="t-h2 cl-h">{t('clo.taxBase')}</h3>
              <Line label={t('clo.profitBeforeTax')} value={fmt(resultBeforeTax)} help={t('clo.profitBeforeTaxHelp')} />
              <Line prefix="+" label={t('clo.permanentDiffsExTax')} value={fmt(permSum)} help={t('clo.permanentDiffsExTaxHelp')} />
              <Line prefix="-" label={t('clo.tempDiffMovement')} value={fmt(midlEndring)} help={t('clo.tempDiffMovementHelp')} />
              <Line prefix="+" label={t('clo.lossCarryforward')} value={fmt(lossCarry - lossApplied)} help={t('clo.priorLossUsedHelp')} />
              <Line prefix="=" label={t('clo.taxBase')} value={fmt(taxable)} strong help={t('clo.taxBaseResultHelp')} />
              <Line prefix="×" label={t('clo.taxRate')} value={taxRuleSupported ? Math.round(taxRate * 100) + ' %' : t('clo.mustBeClarified')} help={t('clo.corporateRateHelp')} />
              <Line prefix="=" label={t('clo.taxPayable')} value={taxRuleSupported ? fmt(taxAmount) : t('clo.notCalculated')} strong help={t('clo.taxToPayHelp')} />
            </div>

            {!personalTaxFlow && <div>
              <h3 className="t-h2 cl-h">{t('clo.deferredTaxCalc')}</h3>
              <Line label={t('clo.temporaryDiffs')} value={fmt(midlForskjell)} help={t('clo.deferredTaxBasisHelp')} />
              <Line prefix="−" label={t('clo.lossCarryforwardShort')} value={fmt(fremforbart)} help={t('clo.lossCarryforwardShortHelp')} />
              <Line prefix="=" label={t('clo.sum')} value={fmt(sumDTA)} strong />
              <AmountEditRow prefix="+" label={t('clo.unrecognisedDeferredAsset')} value={ikkeBokfort} onSave={saveDtaOverride} />
              <Line prefix="=" label={t('clo.deferredTaxBasis')} value={fmt(grunnlagDTA)} strong />
              <Line prefix="×" label={t('clo.taxRate')} value={taxRuleSupported ? Math.round(taxRate * 100) + ' %' : t('clo.mustBeClarified')} />
              <Line prefix="=" label={t('clo.deferredTax')} value={fmt(utsattSkatt)} strong help={t('clo.deferredTaxHelp')} />
            </div>}

            <div className="cl-specs">
              <SpecCard title={t('clo.permanentDiffs')} count={permPosts.length || null} open={openSpec === 'perm'} onToggle={(v) => setOpenSpec(v ? 'perm' : null)} help={t('clo.permanentDiffsHelp')}>
                <p>{t('clo.permanentDiffsIntro')}</p>
                <PermList kind="tillegg" title={t('clo.additionToBusinessIncome')} posts={permPosts} onEdit={openEditPerm} empty={t('clo.noAdditionsRegistered')} />
                <PermList kind="fradrag" title={t('clo.deductionFromBusinessIncome')} posts={permPosts} onEdit={openEditPerm} empty={t('clo.noDeductionsRegistered')} />
                {automaticPermPosts.map((post) => <div key={post.id} className="panel soft row between"><span><b>{post.label}</b><span className="t-sm muted" style={{ display: 'block' }}>{t('clo.calculatedFromAccountCannotEdit', { code: post.sourceAccountCode })}</span></span><b className="t-num">{post.kind === 'fradrag' ? '−' : '+'}{fmt(post.amount)}</b></div>)}
                <div className="cl-picker-wrap" style={{ alignSelf: 'flex-end' }}>
                  <Button size="sm" variant="secondary" icon="plus" iconRight="chevDown" onClick={() => setPickerOpen((p) => !p)} data-help-title={t('clo.addPermanentDiffItem')} data-help={t('clo.addPermanentDiffItemHelp')}>{t('clo.addPermanentDiff')}<span className="cl-addbtn-suffix">{t('r1c.addSuffix')}</span></Button>
                  {pickerOpen ? <Picker variant="perm" permanentAdditionPosts={availablePermanentAdditions} permanentDeductionPosts={availablePermanentDeductions} onPick={onPickPerm} onClose={() => setPickerOpen(false)} /> : null}
                </div>
              </SpecCard>

              <SpecCard title={t('clo.temporaryDiffs')} count={midlPosts.length || null} open={openSpec === 'midl'} onToggle={(v) => setOpenSpec(v ? 'midl' : null)} help={t('clo.temporaryDiffsHelp')}>
                <p>{t('clo.temporaryDiffsIntro1')}</p>
                <p>{t('clo.temporaryDiffsIntro2')}</p>
                <MidlList posts={midlPosts} onEdit={openEditMidl} />
                {automaticMidlPosts.map((post)=><div key={post.id} className="panel soft row between"><span><b>{post.label}</b><span className="t-sm muted" style={{display:'block'}}>{t('clo.calculatedFromAssetRegisterCannotEdit')}</span></span><b className="t-num">{fmt((post.rmAar||0)-(post.smAar||0))}</b></div>)}
                <div className="cl-picker-wrap" style={{ alignSelf: 'flex-end' }}>
                  <Button size="sm" variant="secondary" icon="plus" iconRight="chevDown" onClick={() => setMidlPicker((p) => !p)} data-help-title={t('clo.addTempDiffItem')} data-help={t('clo.addTempDiffItemHelp')}>{t('clo.addTempDiff')}<span className="cl-addbtn-suffix">{t('r1c.addSuffix')}</span></Button>
                  {midlPicker ? <Picker variant="midl" temporaryPosts={availableTemporaryDifferences} onPick={onPickMidl} onClose={() => setMidlPicker(false)} /> : null}
                </div>
              </SpecCard>

              <SpecCard title={t('clo.lossCarryforward')} open={openSpec === 'underskudd'} onToggle={(v) => setOpenSpec(v ? 'underskudd' : null)} help={t('clo.lossCarryforwardLongHelp')}>
                <p>{t('clo.lossCarryforwardIntro')}</p>
                <div className="cl-usk">
                  <UskRow label={t('clo.priorLossCarried')} value={usk.fremfortTidl} onSave={(v) => saveUskPatch({ fremfortTidl: v == null ? 0 : v, openingConfirmed: true, openingSource: 'manual' })} help={t('clo.priorLossCarriedHelp')} />
                  {taxModel.readiness?.expectedPriorLossCarryforward == null && !usk.openingConfirmed ? <div className="ib-warning">{t('clo.missingPriorTaxYear')}</div> : null}
                  <TaxNotCalculatedNote /><UskRow label={t('clo.debtSettlement')} value={usk.underhand} onSave={(v) => saveUskPatch({ underhand: v })} help={t('clo.debtSettlementHelp')} />
                  <UskRow label={t('clo.debtSettlementRest')} value={usk.restUnderhand} onSave={(v) => saveUskPatch({ restUnderhand: v })} help={t('clo.debtSettlementRestHelp')} />
                  <UskRow label={t('clo.lossUsedThisYear')} value={taxCalc.lossUseCalculated ? lossApplied : usk.aaretsAnv} computed={taxCalc.lossUseCalculated} onSave={(v) => saveUskPatch({ aaretsAnv: v == null ? 0 : v })} help={taxCalc.lossUseCalculated ? t('clo.lossUsedThisYearCalculatedHelp') : t('clo.lossUsedThisYearHelp')} />
                  <UskRow label={t('clo.lossCarryforwardInIncome')} value={fremfortInntekt} computed help={t('clo.lossCarryforwardInIncomeHelp')} />
                </div>
              </SpecCard>

              {extraSpecs.map((title) => <SpecCard key={title} title={title} open={openSpec === title} onToggle={(v) => setOpenSpec(v ? title : null)}>
                {window.PlummeTaxBasisCalculation.isNotCalculatedSpec(title) ? <TaxNotCalculatedNote /> : null}<ExtraSpecContent title={title} value={extraSpecValues[title] || {}} onChange={(value) => saveExtraSpecValue(title, value)} onRemove={() => removeExtraSpec(title)} />
              </SpecCard>)}
            </div>

            <div className="cl-picker-wrap" style={{ alignSelf: 'flex-start' }}>
              <Button variant="secondary" icon="plus" iconRight="chevDown" onClick={() => setSpecMenu((s) => !s)}>{t('clo.addSpecification')}</Button>
              {specMenu ? <Popover align="left" width={400} onClose={() => setSpecMenu(false)}>
                <div className="cl-picker-list">
                  {extraSpecs().map((title) => <button key={title} className="cl-picker-item" onClick={() => addExtraSpec(title)}><span>{title}</span></button>)}
                </div>
              </Popover> : null}
            </div>

            <PermDiffModal draft={permDraft} additionPosts={availablePermanentAdditions} deductionPosts={availablePermanentDeductions} onChange={setPermDraft} onClose={() => setPermDraft(null)} onSave={savePerm} onDelete={permDraft && permDraft.id ? deletePerm : null} />
            <MidlDiffModal draft={midlDraft} posts={availableTemporaryDifferences} onChange={setMidlDraft} onClose={() => setMidlDraft(null)} onSave={saveMidl} onDelete={midlDraft && midlDraft.id ? deleteMidl : null} />
          </>}
          {active === 'wealth' && <><TaxWealthBasisNotice wealth={wealth} /><TaxWealthValuationEditor valuation={wealth.valuation} year={year}/>
            <h3 className="t-h2 cl-h">{t('clo.overview')}</h3>
            <div>
              <WealthRow label={t('clo.assetsAndInventory')} value={fmt(wealth.assetsInventoryTotal || 0)} onDetails={() => setOpenWealth('assets')} help={t('clo.wealth.assetsHelp')} />
              <WealthRow prefix="+" label={t('clo.sharesAndInvestments')} value={fmt(wealth.sharesInvestmentTotal || 0)} onDetails={() => setOpenWealth('shares')} help={t('clo.wealth.sharesHelp')} />
              <WealthRow prefix="+" label={t('clo.otherWealth')} value={fmt(wealth.otherAssetsTotal || 0)} onDetails={() => setOpenWealth('other')} help={t('clo.wealth.otherHelp')} />
              <WealthRow prefix="=" label={t('clo.wealth.total')} value={fmt(wealth.totalAssets || 0)} strong help={t('clo.wealth.totalHelp')} />
              <WealthRow label={t('clo.wealth.otherDebt')} value={fmt(wealth.totalDebt || 0)} onDetails={() => setOpenWealth('debt')} help={t('clo.wealth.otherDebtHelp')} />
              <WealthRow label={t('clo.totalDebt')} value={fmt(wealth.totalDebt || 0)} onDetails={() => setOpenWealth('debt')} strong help={t('clo.wealth.totalDebtHelp')} />
              <WealthRow label={t('clo.sumWealthAndDebtAtYearEnd', { year })} value={fmt(wealth.netWealth || 0)} strong big help={t('clo.wealth.netHelp')} />
            </div>
            {!wealth.reviewRequired && !taxModel.isEnk && (wealth.netWealth || 0) < 0 ? <p className="cl-wnote">{t('clo.limitedCompanyNoNegativeWealth')}</p> : null}

            <h3 className="t-h2 cl-h">{t('clo.details')}</h3>
            <div className="cl-specs">
              <SpecCard title={t('clo.assetsAndInventory')} open={openWealth === 'assets'} onToggle={(v) => setOpenWealth(v ? 'assets' : null)} help={t('clo.wealth.assetsLongHelp')}>
                <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.assetInventoryRows || []} sum={wealth.assetsInventoryTotal || 0} />
              </SpecCard>

              <SpecCard title={t('clo.sharesAndInvestments')} open={openWealth === 'shares'} onToggle={(v) => setOpenWealth(v ? 'shares' : null)} help={t('clo.wealth.sharesLongHelp')}>
                <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.sharesInvestmentRows || []} sum={wealth.sharesInvestmentTotal || 0} empty={t('clo.noSharesOrInvestmentsInTrialBalance')} />
              </SpecCard>

              <SpecCard title={t('clo.otherWealth')} open={openWealth === 'other'} onToggle={(v) => setOpenWealth(v ? 'other' : null)} help={t('clo.wealth.otherLongHelp')}>
                <WealthTable valueHead={t('asset.accountingValue')} rows={wealth.otherAssetRows || []} sum={wealth.otherAssetsTotal || 0} />
              </SpecCard>

              <SpecCard title={t('clo.debt')} open={openWealth === 'debt'} onToggle={(v) => setOpenWealth(v ? 'debt' : null)} help={t('clo.wealth.debtLongHelp')}>
                <WealthTable labelHead={t('clo.debtRelatedTo')} valueHead={t('clo.valueColumn')} rows={wealth.debtRows || []} sum={wealth.totalDebt || 0} empty={t('clo.noDebtItemsInTrialBalance')} />
              </SpecCard>
            </div>
          </>}
          {active === 'assets' && <>
            <TaxDepreciationElection year={year} />
            <TaxNotCalculatedNote kind="assets" /><TaxAssetTable type="saldo" items={taxAssetGroups.saldo} onOpen={openTaxAsset} />
            <TaxAssetTable type="linear" items={taxAssetGroups.linear} onOpen={openTaxAsset} />
            <TaxAssetTable type="none" items={taxAssetGroups.none} onOpen={openTaxAsset} />
            <TaxAssetTable type="gtk" items={taxAssetGroups.gtk} onOpen={openTaxAsset} />
            {!assetItems.length ? <p className="cl-empty">{t('clo.noFixedAssetsRegisteredForYear', { year })}</p> : null}
            <div className="ei-actions">
              <TaxAssetAddMenu onPick={openNewTaxAsset} />
              <Button variant="secondary" icon="plus" onClick={() => openNewTaxAsset('gtk')}>{t('clo.addGainLossAccount')}</Button>
            </div>
            <TaxAssetModal draft={assetDraft} onChange={setAssetDraft} onClose={() => setAssetDraft(null)} onSave={saveTaxAssetDraft} onDelete={assetDraft?.id ? deleteTaxAssetDraft : null} />
          </>}
          {active === 'other' && <>
            {taxModel.isEnk ? <CheckCard title={t('clo.enkPersonalTaxReturnTitle')} defaultOpen>
              <p>{t('clo.personalTaxReturnAndBusinessIncomeBody')}</p>
              <div className="cl-detail">
                <DetailRow label={t('clo.businessIncomeFromDraft')} value={fmt(summary.netBusinessIncome || 0)} />
                <DetailRow label={t('clo.personalTaxReturnChecked')} value={soleTraderInfo.personalReturnReviewed ? t('common.yes') : t('common.no')} />
                <DetailRow label={t('clo.spouseAllocationDetailStatus')} value={soleTraderInfo.spouseAllocationStatus === 'not_applicable' ? t('clo.spouseAllocationNotApplicable') : soleTraderInfo.spouseAllocationStatus === 'reviewed' ? t('clo.spouseAllocationReviewedShort') : t('clo.mustBeClarified')} />
                <DetailRow label={t('clo.duplicateIncomeCheckedLabel')} value={soleTraderInfo.duplicateIncomeChecked ? t('common.yes') : t('common.no')} />
              </div>
              <div className="ans-card-foot"><Button size="sm" variant="secondary" onClick={() => setOtherEdit({ kind: 'soleTrader', title: t('clo.enkPersonalTaxReturnTitle'), draft: { ...soleTraderInfo } })}>{t('clo.fillInChecks')}</Button></div>
            </CheckCard> : <>
            <CheckCard title={t('clo.ownerTransactions')} defaultOpen>
              <p>{t('clo.ownerTransactionsIntro')}</p>
              <div className="cl-detail">
                <DetailRow label={t('clo.dividendPaidInYear', { year })} value={otherFmt('num', ownerBenefits.dividendPaid)} />
                <DetailRow label={t('clo.ownerLoanToOwner')} value={ownerBenefits.companyToOwnerBenefit || t('common.no')} />
                <DetailRow label={t('clo.ownerLoanFromOwner')} value={ownerBenefits.ownerToCompanyBenefit || t('common.no')} />
                {ownerBenefits.notes ? <DetailRow label={t('pay.note')} value={ownerBenefits.notes} /> : null}
              </div>
              <div className="ans-card-foot"><Button size="sm" variant="secondary" onClick={() => setOtherEdit({ kind: 'owner', title: t('clo.ownerBenefitsBetweenCompanyAndOwnerTitle'), draft: { ...ownerBenefits } })}>{t('clo.editDetails')}</Button></div>
            </CheckCard>
            <CheckCard title={t('clo.otherCompanyDetails')} defaultOpen>
              <p>{t('clo.otherCompanyDetailsHelp')}</p>
              <div className="cl-subhead">{t('clo.ownership')}</div>
              <div className="cl-detail">
                <DetailRow label={t('clo.foreignOwnership')} value={companyOtherInfo.foreignOwnershipOrCrossBorderReorg || t('common.no')} />
                <DetailRow label={t('clo.wasSubsidiary')} value={companyOtherInfo.wasGroupSubsidiary || t('common.no')} />
              </div>
              <div className="cl-subhead" style={{ marginTop: 12 }}>{t('set.bank.typeOther')}</div>
              <div className="cl-detail">
                <DetailRow label={t('clo.taxFreeTransfers')} value={companyOtherInfo.taxFreeTransferOfAssetsOrLiabilities || t('common.no')} />
                {companyOtherInfo.notes ? <DetailRow label={t('pay.note')} value={companyOtherInfo.notes} /> : null}
              </div>
              <div className="ans-card-foot"><Button size="sm" variant="secondary" onClick={() => setOtherEdit({ kind: 'company', title: t('clo.otherCompanyInfoTitle'), draft: { ...companyOtherInfo } })}>{t('clo.editDetails')}</Button></div>
            </CheckCard>
            </>}
            <CheckCard title={t('clo.advanceTaxAndSettlementTitle')} defaultOpen>
              <p>{t('clo.advanceTaxAndSettlementBody')}</p>
              <div className="cl-detail">
                <DetailRow label={t('clo.advancePlusAdditionalPaid')} value={fmt(Number(taxSettlement.advanceTaxPaid || 0) + Number(taxSettlement.additionalAdvanceTax || 0))} />
                <DetailRow label={t('clo.estimatedDifferenceVsCalculatedTax')} value={taxSettlementModel.estimatedDifference == null ? '-' : fmt(taxSettlementModel.estimatedDifference)} />
                <DetailRow label={t('clo.settlementStatusLabel')} value={taxSettlement.status === 'settled' ? t('clo.taxSettlementSettled') : taxSettlement.status === 'received' ? t('clo.settlementReceivedShort') : t('clo.settlementNotReceivedShort')} />
                <DetailRow label={t('clo.finalAssessedTax')} value={taxSettlement.finalAssessedTax == null ? '-' : fmt(taxSettlement.finalAssessedTax)} />
              </div>
              <div className="ans-card-foot"><Button size="sm" variant="secondary" onClick={() => setOtherEdit({ kind: 'settlement', title: t('clo.advanceTaxAndSettlementTitle'), draft: { ...taxSettlement } })}>{t('clo.updateSettlement')}</Button></div>
            </CheckCard>
            {!taxModel.isEnk && <><h3 className="t-h2 cl-h">{t('clo.companyAndOwnership')}</h3>
            <CheckCard title={t('clo.fillSharesInfo')} defaultOpen>
              <p>{t('clo.fillSharesInfoBody')}</p>
            </CheckCard>
            {otherInvestments.length ? otherInvestments.map((investment) => <OtherInvestmentTable key={investment.id} investment={investment} onOpen={openOtherInvestment} />) : <p className="cl-empty">{t('clo.noSharesOrInvestmentsFound')}</p>}
            <div className="ei-actions">
              <OtherAddMenu label={t('clo.addShare')} items={otherAddShares()} onPick={openNewOtherInvestment} />
              <OtherAddMenu label={t('clo.addOtherInvestments')} items={otherAddInvestments()} onPick={openNewOtherInvestment} />
            </div></>}
            {otherEdit ? <OtherSimpleModal
              title={otherEdit.title}
              draft={otherEdit.draft}
              rows={otherEdit.kind === 'owner' ? ownerBenefitRows : otherEdit.kind === 'company' ? companyOtherRows : otherEdit.kind === 'soleTrader' ? soleTraderRows : taxSettlementRows}
              onChange={(draft) => setOtherEdit({ ...otherEdit, draft })}
              onClose={() => setOtherEdit(null)}
              onSave={otherEdit.kind === 'owner' ? saveOwnerBenefits : otherEdit.kind === 'company' ? saveCompanyOtherInfo : otherEdit.kind === 'soleTrader' ? saveSoleTraderInfo : saveTaxSettlement}
            /> : null}
            <OtherInvestmentModal draft={otherDraft} onChange={setOtherDraft} onClose={() => setOtherDraft(null)} onSave={saveOtherInvestmentDraft} onDelete={otherDraft?.id ? deleteOtherInvestmentDraft : null} />
          </>}
          {active === 'income-balance' && <>
            <h2 className="ib-h">{t('clo.incomeStatement')}</h2>
            <TaxIbSection title={t('clo.operatingIncome')} rows={annualIncome.salesIncomeRows || []} totalLabel={t('clo.sumSalesIncome')} />
            {(annualIncome.otherOperatingIncomeRows || []).length ? <TaxIbSection title={t('clo.otherOperatingIncome')} rows={annualIncome.otherOperatingIncomeRows || []} totalLabel={t('clo.sumOtherOperatingIncome')} /> : null}
            <TaxIbRow label={t('clo.totalOperatingIncome')} value={annualIncome.operatingIncome || 0} strong head />
            <TaxIbSection title={t('clo.goodsCost')} rows={annualIncome.productCostRows || []} totalLabel={t('clo.sumGoodsCost')} />
            {(annualIncome.payrollCostRows || []).length ? <TaxIbSection title={t('clo.payrollCost')} rows={annualIncome.payrollCostRows || []} totalLabel={t('clo.sumPayrollCost')} /> : null}
            {(annualIncome.depreciationRows || []).length ? <TaxIbSection title={t('asset.depreciation')} rows={annualIncome.depreciationRows || []} totalLabel={t('clo.sumDepreciation')} /> : null}
            <TaxIbSection title={t('clo.otherOperatingCost')} rows={annualIncome.otherOperatingCostRows || []} totalLabel={t('clo.sumOtherOperatingCost')} />
            <TaxIbRow label={t('clo.totalOperatingCost')} value={annualIncome.operatingCosts || 0} strong head />
            {(annualIncome.financeIncomeRows || []).length ? <TaxIbSection title={t('clo.financialIncome')} rows={annualIncome.financeIncomeRows || []} totalLabel={t('clo.sumFinanceIncome')} /> : null}
            {(annualIncome.financeCostRows || []).length ? <TaxIbSection title={t('clo.financialCost')} rows={annualIncome.financeCostRows || []} totalLabel={t('clo.sumFinanceCost')} /> : null}
            <TaxIbRow label={t('clo.resultForYear')} value={annualIncome.annualResult ?? annualIncome.resultBeforeTax ?? resultBeforeTax} strong head />

            <h2 className="ib-h" style={{ marginTop: 28 }}>{t('clo.balanceSheet')}</h2>
            <div className="ib-sub ib-sub-major">{t('asset.title')}</div>
            {(annualBalance.fixedAssetGroups || []).map((group) => <TaxIbSection key={group.key || group.label} title={group.label} rows={group.rows || []} totalLabel={`Sum ${String(group.label || '').toLowerCase()}`} />)}
            {(annualBalance.currentAssetGroups || []).map((group) => <TaxIbSection key={group.key || group.label} title={group.label} rows={group.rows || []} totalLabel={`Sum ${String(group.label || '').toLowerCase()}`} />)}
            <TaxIbRow label={t('clo.totalAssets')} value={annualBalance.totalAssets || 0} strong head />
            <div className="ib-sub ib-sub-major">{t('clo.equityAndDebt')}</div>
            {(annualBalance.equityGroups || []).map((group) => <TaxIbSection key={group.key || group.label} title={group.label} rows={group.rows || []} totalLabel={`Sum ${String(group.label || '').toLowerCase()}`} />)}
            {(annualBalance.debtGroups || []).map((group) => <TaxIbSection key={group.key || group.label} title={group.label} rows={group.rows || []} totalLabel={`Sum ${String(group.label || '').toLowerCase()}`} />)}
            <TaxIbRow label={t('clo.totalEquityAndDebt')} value={annualBalance.totalEquityAndDebt || 0} strong head />
            {Math.abs(Number(annualBalance.difference || 0)) > 0.005 ? <div className="ib-warning">{t('clo.balanceDiscrepancyWithAmount', { amount: fmt(annualBalance.difference) })}</div> : null}

            <h2 className="ib-h" style={{ marginTop: 28 }}>{t('clo.specification')}</h2>
            <div className="col gap-12">
              <TaxIbSpec title={t('clo.inventoryValue')} value={taxIbRowsTotal(ibInventoryRows)} open={ibOpen === 'inventory'} onToggle={() => setIbOpen(ibOpen === 'inventory' ? null : 'inventory')}>
                <TaxIbInventoryPanel spec={ibInventorySpec} onSave={(values) => saveIbSpecValues('inventory', t('clo.inventoryValueTitle'), values)} />
              </TaxIbSpec>
              <TaxIbSpec title={t('clo.receivableTaxValue')} value={taxIbRowsTotal(ibReceivableRows) + taxIbRowsTotal(ibOtherReceivableRows)} open={ibOpen === 'receivables'} onToggle={() => setIbOpen(ibOpen === 'receivables' ? null : 'receivables')}>
                <TaxIbReceivablesPanel spec={ibReceivablesSpec} receivableRows={ibReceivableRows} otherRows={ibOtherReceivableRows} onSave={(values) => saveIbSpecValues('receivables', t('clo.receivableTaxValueTitle'), values)} />
              </TaxIbSpec>
              <TaxIbSpec title={t('clo.equity')} value={annualBalance.totalEquity || 0} open={ibOpen === 'equity'} onToggle={() => setIbOpen(ibOpen === 'equity' ? null : 'equity')}>
                <TaxIbEquityPanel totalEquity={annualBalance.totalEquity || 0} annualResult={annualIncome.annualResult ?? annualIncome.resultBeforeTax ?? resultBeforeTax} equityChanges={ibEquityChanges} principleChanges={ibPrincipleChanges} accountOptions={ibAccountOptions} onEquityAdd={addIbEquityChange} onEquityDraft={setIbEquityDraft} onPrincipleDraft={setIbPrincipleDraft} />
              </TaxIbSpec>
              <TaxIbSpec title={t('clo.payrollPensionSpec')} value={taxIbRowsTotal(annualIncome.payrollCostRows || [])} open={ibOpen === 'payroll'} onToggle={() => setIbOpen(ibOpen === 'payroll' ? null : 'payroll')}>
                <TaxIbPayrollPanel spec={ibPayrollSpec} payrollRows={annualIncome.payrollCostRows || []} onSave={(values) => saveIbSpecValues('payroll', t('clo.payrollPensionSpecTitle'), values)} onOpenSpec={() => setIbSpecDraft({ ...(ibPayrollSpec || { id: null, type: 'payroll', title: t('clo.payrollPensionSpecTitle'), values: {} }), values: { ...(ibPayrollSpec?.values || {}) } })} />
              </TaxIbSpec>
            </div>
            {(ibPosts.length || ibExtraSpecs.length) ? <div className="ib-added">
              {ibPosts.map((post) => <div key={post.id} className="ib-frow ib-added-row"><span><span className="ib-added-tag">{post.kind === 'income' ? t('clo.incomeKind') : post.kind === 'cost' ? t('clo.costKind') : t('clo.balanceKind')}</span>{post.code} - {post.label}</span><b>{post.amount == null ? '-' : fmt(post.amount)}</b><button className="ib-frow-act ib-frow-endre" onClick={() => setIbPostDraft({ ...post })}>{t('clo.change')}</button></div>)}
              {ibExtraSpecs.map((spec) => <div key={spec.id} className="ib-frow ib-added-row"><span><span className="ib-added-tag">{t('clo.specification')}</span>{spec.title}</span><b>{spec.values?.amount == null ? '' : fmt(spec.values.amount)}</b><button className="ib-frow-act ib-frow-endre" onClick={() => setIbSpecDraft({ ...spec, values: { ...(spec.values || {}) } })}>{t('clo.change')}</button></div>)}
            </div> : null}
            <div className="ib-bottombtns row gap-10" style={{ flexWrap: 'wrap' }}>
              <TaxIbAccountMenu label={t('clo.addItem')} accountOptions={ibAccountOptions} onPick={openNewIbPost} help={t('clo.addItemHelp')} />
              <TaxIbAddMenu label={t('clo.addSpecification')} items={taxIbExtraSpecTypes()} onPick={openNewIbSpec} />
            </div>
            <TaxIbPostModal draft={ibPostDraft} onChange={setIbPostDraft} onClose={() => setIbPostDraft(null)} onSave={saveIbPostDraft} onDelete={ibPostDraft?.id ? deleteIbPostDraft : () => setIbPostDraft(null)} accountOptions={ibAccountOptions} />
            <TaxIbSpecModal draft={ibSpecDraft} onChange={setIbSpecDraft} onClose={() => setIbSpecDraft(null)} onSave={saveIbSpecDraft} onDelete={ibSpecDraft?.id ? deleteIbSpecDraft : null} />
            <TaxIbEquityChangeModal draft={ibEquityDraft} onChange={setIbEquityDraft} onClose={() => setIbEquityDraft(null)} onSave={saveIbEquityDraft} onDelete={ibEquityDraft?.id ? deleteIbEquityDraft : null} />
            <TaxIbPrincipleModal draft={ibPrincipleDraft} onChange={setIbPrincipleDraft} onClose={() => setIbPrincipleDraft(null)} onSave={saveIbPrincipleDraft} onDelete={ibPrincipleDraft?.id ? deleteIbPrincipleDraft : null} />
          </>}
          {active === 'all' && <>
            <div className="cl-all-grid">
              {taxAllSummaryCards.map((card) => <div key={card.id} className="cl-all-card">
                <div className="cl-all-card-h">
                  <div>
                    <h4>{card.title}</h4>
                    <span className={'cl-status-pill ' + allStatusClass(card.status)}><span className="dot"></span>{card.status}</span>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setAllDetails(card.id)}>{t('clo.viewDetails')}</Button>
                </div>
                <div className="cl-all-facts">
                  {card.rows.slice(0, 4).map(([label, value]) => <React.Fragment key={`${card.id}-${label}`}>
                    <span>{label}</span><strong>{value}</strong>
                  </React.Fragment>)}
                </div>
              </div>)}
            </div>
            {!taxBalanceOk ? <InfoBox title={t('clo.balanceDiscrepancy')}>{t('clo.balanceDiscrepancyBody', { amount: fmt(annualBalance.difference || 0) })}</InfoBox> : null}
            <Modal open={Boolean(allDetails)} onClose={() => setAllDetails(null)} title={taxAllCardById(allDetails)?.title || t('clo.detailsFallback')} max={820}
              footer={<Button variant="secondary" onClick={() => setAllDetails(null)}>{t('clo.close')}</Button>}>
              {renderTaxAllDetails()}
            </Modal>
          </>}
        </StepAccordion>
      </Card>

      <Rail title={t('clo.filing')} steps={TAX_RAIL} stage={stage} mascotImg={TAX_RAIL_IMAGES[stage] || 'src/img/plommeMagnifier.png'}>
        {stage === 0 && <p>{t('clo.reviewStepsIntro')}</p>}
        {stage === 1 && <>
          <p>{t('clo.readyForReview')}</p>
          <div className="cl-rail-actions"><Button variant="secondary" icon="arrowLeft" onClick={() => { setStage(0); void saveTaxProgress({ phase: 'draft', submissionStage: 'review' }); }}>{t('clo.back')}</Button><Button icon="check" disabled={!readiness.ready} onClick={() => { setStage(2); void saveTaxProgress({ phase: 'draft', submissionStage: 'ready' }); }}>{t('clo.checkBusinessInfo')}</Button></div>
        </>}
        {stage === 2 && <>
          <p>{t('clo.internalRulesOnly')}</p>
          <div className="cl-rail-actions"><Button variant="secondary" icon="arrowLeft" onClick={() => { setStage(1); void saveTaxProgress({ phase: 'draft', submissionStage: 'checked' }); }}>{t('clo.back')}</Button><Button icon="sendCheck" disabled={!taxYearClosed} onClick={() => { setStage(3); void saveTaxProgress({ phase: 'draft', submissionStage: 'ready' }); }}>{t('clo.reviewFiling')}</Button></div>
        </>}
        {stage === 3 && <>
          <p>{t('clo.processingStepPlaceholder')}</p>
          <div className="cl-rail-actions"><Button variant="secondary" icon="arrowLeft" onClick={() => { setStage(2); void saveTaxProgress({ phase: 'draft', submissionStage: 'ready' }); }}>{t('clo.back')}</Button><Button icon="sendCheck" disabled data-help={t('helpTopics.taxSubmission.body')} data-help-title={t('helpTopics.taxSubmission.title')}>{t('clo.filingIntegrationUnavailable', 'Innsending er ikke koblet')}</Button></div>
        </>}
        {stage === 4 && <div className="cl-annual-done"><p>{t('clo.taxReturnFiled')}</p><div className="cl-rail-actions"><Button variant="secondary" icon="refresh" onClick={reopenTaxReturn}>{t('clo.cancelTaxReturn')}</Button></div></div>}
      </Rail>
    </div>
  </div>;
}

// ============================================================
// ÅRSREGNSKAP (annual accounts)
// ============================================================
const aaSteps = () => [
  { id: 'about', label: t('clo.aboutAnnualAccounts') },
  { id: 'income', label: t('clo.incomeStatement') },
  { id: 'balance', label: t('clo.balanceSheet') },
  { id: 'notes', label: t('clo.notes') },
  { id: 'all', label: t('clo.allInformation') },
];
const aaRail = () => [
  t('clo.annualRail.0', 'Se gjennom og fyll ut'),
  t('clo.annualRail.1', 'Dokumenter'),
  t('clo.annualRail.2', 'Signering'),
  t('clo.annualRail.3', 'Send til Brønnøysund'),
  t('clo.annualRail.4', 'Ferdig'),
];
const AA_RAIL_IMAGES = ['src/img/plommeMagnifier.png', 'src/img/PlummaLevering.png', 'src/img/plummeCheck.png', 'src/img/PostPlumma.png', 'src/img/PlummaCheer.png'];
const AA_RAIL_IMAGE_STYLE = { width: 190, height: 190 };
const AA_RAIL_IMAGE_STYLES = [
  AA_RAIL_IMAGE_STYLE,
  { width: 155, height: 146 },
  { width: 209, height: 209 },
  { width: 174, height: 190 },
  { width: 235, height: 235 },
];
const annualAccountsReopenPatch = () => ({
  submissionStage: 'review',
  activeStepId: 'about',
  confirmedStepIds: [],
  annualAccountsDocumentPrepared: false,
  boardProtocolPrepared: false,
  generalAssemblyProtocolPrepared: false,
  annualAccountsDocumentSigned: false,
  boardProtocolSigned: false,
  generalAssemblyProtocolSigned: false,
  annualAccountsSigned: false,
});
// Hvert dokument går klar -> sendt -> signert. `...Prepared` beholdes som «sendt til
// signering» slik at status som allerede er lagret på foretaket fortsatt gjelder.
// Bygges som en funksjon (ikke en modulnivå-konstant) slik at t()-oppslagene under
// leses med aktivt språk hver gang modellen brukes, i stedet for å fryses ved skriptlasting.
const emptyAnnualAccountsModel = () => ({
  company: { name: t('clo.myBusinessFallback', 'Mitt foretak'), orgnr: '', email: '' },
  companyType: 'AS',
  isEnk: false,
  process: { submissionStage: 'review', stageIndex: 0, activeStepId: 'about', confirmedStepIds: [] },
  submission: { companyLabel: t('clo.myBusinessFallback', 'Mitt foretak'), contactEmail: '', accountingPeriod: '', representativeName: t('clo.myBusinessFallback', 'Mitt foretak'), accountingRuleType: 'Regnskapslovens regler for små foretak' },
  incomeStatement: { incomeRows: [], salesIncomeRows: [], otherOperatingIncomeRows: [], costRows: [], productCostRows: [], payrollCostRows: [], depreciationRows: [], otherOperatingCostRows: [], financeIncomeRows: [], financeCostRows: [], taxRows: [], dispositionRows: [], operatingIncome: 0, operatingCosts: 0, operatingResult: 0, netFinance: 0, resultBeforeTax: 0, taxCost: 0, annualResult: 0 },
  balanceSheet: { fixedAssetGroups: [], fixedAssets: [], currentAssetGroups: [], currentAssets: [], equityGroups: [], equityRows: [], debtGroups: [], debtRows: [], totalFixedAssets: 0, totalCurrentAssets: 0, totalAssets: 0, totalEquity: 0, totalDebt: 0, totalEquityAndDebt: 0, difference: 0 },
  notes: [],
});
// {value, label} par: value er den lagrede/sammenlignede verdien (uendret), label vises oversatt.
const goingConcernReasons = () => [
  { value: 'Ikke usikkerhet, skal få bedre økonomi', label: t('clo.goingConcernReason.noUncertaintyImproving', 'Ikke usikkerhet, skal få bedre økonomi') },
  { value: 'Eiere vil tilføre mer penger', label: t('clo.goingConcernReason.ownersWillContribute', 'Eiere vil tilføre mer penger') },
  { value: 'Det er merverdier i regnskapet', label: t('clo.goingConcernReason.hiddenValues', 'Det er merverdier i regnskapet') },
  { value: 'Selskapet er i en oppstartsfase', label: t('clo.goingConcernReason.startupPhase', 'Selskapet er i en oppstartsfase') },
  { value: 'Søker ekstern finansiering', label: t('clo.goingConcernReason.seekingFinancing', 'Søker ekstern finansiering') },
  { value: 'Egen tekst', label: t('clo.goingConcernReason.ownText', 'Egen tekst') },
];
const getAnnualAccountsPageModel = (year) => {
  if (window.PlummeBackend?.getAnnualAccountsModel) return window.PlummeBackend.getAnnualAccountsModel({ year });
  const empty = emptyAnnualAccountsModel();
  return {
    ...empty,
    year,
    submission: {
      ...empty.submission,
      accountingPeriod: `01.01.${year} - 31.12.${year}`,
      accountingYear: year,
    },
  };
};

function ArsregnskapPage({ year = 2025, setRoute, setYear }) {
  const AA_STEPS = aaSteps();
  const AA_RAIL = aaRail();
  const GOING_CONCERN_REASONS = goingConcernReasons();
  const EMPTY_ANNUAL_ACCOUNTS_MODEL = emptyAnnualAccountsModel();
  const backend = window.PlummeBackend?.useBackend ? window.PlummeBackend.useBackend() : null;
  const model = React.useMemo(() => getAnnualAccountsPageModel(year), [backend?.appState, backend?.activeBusinessId, year]);
  const company = model.company || EMPTY_ANNUAL_ACCOUNTS_MODEL.company;
  const submission = model.submission || EMPTY_ANNUAL_ACCOUNTS_MODEL.submission;
  const process = model.process || EMPTY_ANNUAL_ACCOUNTS_MODEL.process;
  const annualYearClosed = Boolean(model.status?.yearClosed);
  const income = model.incomeStatement || EMPTY_ANNUAL_ACCOUNTS_MODEL.incomeStatement;
  const balance = model.balanceSheet || EMPTY_ANNUAL_ACCOUNTS_MODEL.balanceSheet;
  const notes = Array.isArray(model.notes) ? model.notes : [];
  const noteById = React.useCallback((id) => notes.find((note) => note.id === id) || null, [notes]);
  const initialGroupNote = notes.find((note) => note.id === 'group') || {};
  const initialWorkYearsNote = notes.find((note) => note.id === 'work-years') || {};
  const [active, setActive] = useState(process.activeStepId || 'about');
  const [done, setDone] = useState(process.confirmedStepIds || []);
  const [stage, setStage] = useState(process.stageIndex || 0);
  const [rfLage, setRfLage] = useState(process.preparedByAccountant || '');
  const [rfGenerelt, setRfGenerelt] = useState(process.assistedByAccountant || '');
  const [revideres, setRevideres] = useState(submission.auditorRequiredLabel || 'Nei');
  const [period, setPeriod] = useState(submission.accountingPeriod || `01.01.${year} - 31.12.${year}`);
  const [representant, setRepresentant] = useState(submission.representativeName || company.name);
  const [contactEmail, setContactEmail] = useState(submission.contactEmail || company.email || '');
  const [principlesOpen, setPrinciplesOpen] = useState(false);
  const [goingConcernAssessment, setGoingConcernAssessment] = useState(process.goingConcernAssessment || 'no_uncertainty');
  const [goingConcernReason, setGoingConcernReason] = useState(process.goingConcernReason || GOING_CONCERN_REASONS[0].value);
  const [goingConcernComment, setGoingConcernComment] = useState(process.goingConcernComment || '');
  const [groupParent, setGroupParent] = useState(process.groupParent ?? Boolean(initialGroupNote.isParent));
  const [groupSubsidiary, setGroupSubsidiary] = useState(process.groupSubsidiary ?? Boolean(initialGroupNote.isSubsidiary));
  const [workYears, setWorkYears] = useState(Number(process.workYears ?? initialWorkYearsNote.workYears ?? 0) || 0);
  const [allDetails, setAllDetails] = useState(null);
  useAnnualAccountsPageControlAdapter({ year, active, setActive, done, stage, yearClosed: annualYearClosed, steps: AA_STEPS, noteCount: notes.length, noteDecisions: model.noteDecisions || [], incomeTotal: income.annualResult, assetTotal: balance.totalAssets, equityAndDebtTotal: balance.totalEquityAndDebt });
  const companyRoles = React.useMemo(
    () => window.PlummeBackend?.getCompanyRoles?.() || [],
    [backend?.appState, backend?.activeBusinessId],
  );
  const shareholders = React.useMemo(
    () => window.PlummeBackend?.getShareholders?.() || [],
    [backend?.appState, backend?.activeBusinessId],
  );
  const annualMeetings = React.useMemo(
    () => window.PlummeBackend?.getAnnualMeetings?.(year) || {},
    [backend?.appState, backend?.activeBusinessId, year],
  );
  const noteSyncYearRef = useRef(year);
  const idx = AA_STEPS.findIndex((s) => s.id === active);
  const reviewDone = done.length === AA_STEPS.length;

  React.useEffect(() => {
    setActive(process.activeStepId || 'about');
    setDone(process.confirmedStepIds || []);
    setStage(process.stageIndex || 0);
    setRfLage(process.preparedByAccountant || '');
    setRfGenerelt(process.assistedByAccountant || '');
    setRevideres(submission.auditorRequiredLabel || 'Nei');
    setPeriod(submission.accountingPeriod || `01.01.${year} - 31.12.${year}`);
    setRepresentant(submission.representativeName || company.name);
    setContactEmail(submission.contactEmail || company.email || '');
    const yearChanged = noteSyncYearRef.current !== year;
    noteSyncYearRef.current = year;
    const groupNote = notes.find((note) => note.id === 'group') || {};
    const workYearsNote = notes.find((note) => note.id === 'work-years') || {};
    setGoingConcernAssessment((current) => yearChanged ? (process.goingConcernAssessment || 'no_uncertainty') : (process.goingConcernAssessment || current || 'no_uncertainty'));
    setGoingConcernReason((current) => yearChanged ? (process.goingConcernReason || GOING_CONCERN_REASONS[0].value) : (process.goingConcernReason || current || GOING_CONCERN_REASONS[0].value));
    setGoingConcernComment((current) => yearChanged ? (process.goingConcernComment || '') : (process.goingConcernComment || current || ''));
    setGroupParent((current) => yearChanged ? (process.groupParent ?? Boolean(groupNote.isParent)) : (process.groupParent ?? current));
    setGroupSubsidiary((current) => yearChanged ? (process.groupSubsidiary ?? Boolean(groupNote.isSubsidiary)) : (process.groupSubsidiary ?? current));
    setWorkYears((current) => yearChanged ? (Number(process.workYears ?? workYearsNote.workYears ?? 0) || 0) : (process.workYears === undefined ? current : Number(process.workYears) || 0));
  }, [backend?.appState, year]);

  const saveProgress = React.useCallback(async (patch, message) => {
    const save = window.PlummeBackend?.saveAnnualAccountsProgress;
    if (!save) return;
    try {
      await save({ year, patch });
      if (message) toast(message, 'success');
    } catch (error) {
      toast(error?.message || t('clo.annualErrorSaving', 'Kunne ikke lagre årsregnskapet.'), 'error');
    }
  }, [year]);

  const updateActive = (stepId) => {
    setActive(stepId);
    void saveProgress({ activeStepId: stepId });
  };
  const chooseAccountant = (field, value) => {
    if (field === 'preparedByAccountant') setRfLage(value);
    if (field === 'assistedByAccountant') setRfGenerelt(value);
    void saveProgress({ [field]: value });
  };
  const chooseGoingConcern = (value) => {
    setGoingConcernAssessment(value);
    void saveProgress({ goingConcernAssessment: value });
  };
  const chooseGoingConcernReason = (value) => {
    setGoingConcernReason(value);
    void saveProgress({ goingConcernReason: value });
  };
  const updateGoingConcernComment = (value) => {
    setGoingConcernComment(value);
    void saveProgress({ goingConcernComment: value });
  };
  const toggleGroupParent = () => {
    setGroupParent((current) => {
      const nextValue = !current;
      void saveProgress({ groupParent: nextValue });
      return nextValue;
    });
  };
  const toggleGroupSubsidiary = () => {
    setGroupSubsidiary((current) => {
      const nextValue = !current;
      void saveProgress({ groupSubsidiary: nextValue });
      return nextValue;
    });
  };
  const setAnnualWorkYears = (value) => {
    const nextValue = Math.max(0, Math.round(Number(value || 0) * 100) / 100);
    setWorkYears(nextValue);
    void saveProgress({ workYears: nextValue });
  };
  const incrementAnnualWorkYears = () => {
    setWorkYears((current) => {
      const nextValue = Math.max(1, Math.round((Number(current || 0) + 1) * 100) / 100);
      void saveProgress({ workYears: nextValue });
      return nextValue;
    });
  };
  const displayedDone = AA_STEPS
    .filter((step, stepIndex) => done.includes(step.id) || (idx > -1 && stepIndex < idx))
    .map((step) => step.id);
  const next = () => {
    const stepsThroughActive = idx > -1 ? AA_STEPS.slice(0, idx + 1).map((step) => step.id) : [active];
    const nd = Array.from(new Set([...done, ...stepsThroughActive].filter(Boolean)));
    setDone(nd);
    const patch = { confirmedStepIds: nd, activeStepId: active };
    if (idx < AA_STEPS.length - 1) {
      const nextStep = AA_STEPS[idx + 1].id;
      setActive(nextStep);
      void saveProgress({ ...patch, activeStepId: nextStep });
    } else if (nd.length === AA_STEPS.length && stage === 0) {
      setStage(1);
      void saveProgress({ ...patch, submissionStage: 'documents' });
    } else {
      void saveProgress(patch);
    }
  };
  const back = () => {
    if (idx <= 0) return;
    updateActive(AA_STEPS[idx - 1].id);
  };
  const moveStage = (nextStage) => {
    setStage(nextStage);
    void saveProgress({ submissionStage: ['review', 'documents', 'signing', 'processing', 'done'][nextStage] || 'review' });
  };
  const reopenAnnualAccounts = () => {
    setActive('about');
    setDone([]);
    setStage(0);
    void saveProgress(annualAccountsReopenPatch(), t('clo.annualReopened'));
  };
  const renderRows = (rows, emptyText = t('clo.noRelevantAccountBalance', 'Ingen saldo på relevante kontoer.')) =>
    rows.length ? rows.map((row) => <Line key={row.code} label={row.label} value={fmt(row.amount)} code={row.code} />) : <p className="muted">{emptyText}</p>;
  const renderAccountDetails = (title, rows, emptyText) =>
    <div className="cl-box cl-account-details">
      <h4>{title}</h4>
      {renderRows(Array.isArray(rows) ? rows : [], emptyText)}
    </div>;
  const renderBalanceGroup = (group) => group ? <React.Fragment key={group.key || group.label}>
    <Line label={group.label} value={fmt(group.total)} strong={false} />
    {Array.isArray(group.rows) && group.rows.length > 1 ? renderRows(group.rows) : null}
  </React.Fragment> : null;
  const principlesNote = noteById('principles');
  const goingConcernNote = noteById('going-concern');
  const fixedAssetsNote = noteById('fixed-assets');
  const groupNote = noteById('group');
  const workYearsNote = noteById('work-years');
  const workYearsLabel = new Intl.NumberFormat(PlummeI18n.intlLocale(), { minimumFractionDigits: workYears % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(workYears);
  const countRows = (rows) => Array.isArray(rows) ? rows.length : 0;
  const balanceOk = Math.abs(balance.difference || 0) <= 0.5;
  const allSummaryCards = [
    {
      id: 'submission',
      title: t('clo.annualSubmissionAndCompany', 'Innsending og selskap'),
      statusCode: [contactEmail, period, representant].every(Boolean) ? 'ready' : 'missingInfo',
      status: [contactEmail, period, representant].every(Boolean) ? t('clo.statusReady', 'Klar') : t('clo.statusMissingInfo', 'Mangler info'),
      rows: [[t('clo.companyLabelShort', 'Selskap'), submission.companyLabel || company.name], [t('clo.financialYearLabel', 'Regnskapsår'), year], [t('clo.accountingPeriod'), period || '-'], [t('clo.representativeLabel', 'Representant'), representant || '-']],
    },
    {
      id: 'accountant',
      title: t('clo.accountantAndAudit'),
      statusCode: [rfLage, rfGenerelt, revideres].every(Boolean) ? 'ready' : 'missingChoice',
      status: [rfLage, rfGenerelt, revideres].every(Boolean) ? t('clo.statusReady', 'Klar') : t('clo.statusMissingChoice', 'Mangler valg'),
      rows: [[t('clo.auditLabel', 'Revisjon'), revideres || '-'], [t('clo.preparedByAccountantLabel', 'Laget av regnskapsfører'), rfLage === 'ja' ? t('common.yes') : rfLage === 'nei' ? t('common.no') : '-'], [t('clo.assistedByAccountantLabel', 'Regnskapsfører gjennom året'), rfGenerelt === 'ja' ? t('common.yes') : rfGenerelt === 'nei' ? t('common.no') : '-']],
    },
    {
      id: 'income',
      title: t('clo.incomeStatement'),
      statusCode: countRows(income.incomeRows) + countRows(income.costRows) > 0 ? 'ready' : 'noEntries',
      status: countRows(income.incomeRows) + countRows(income.costRows) > 0 ? t('clo.statusReady', 'Klar') : t('clo.statusNoEntries', 'Ingen poster'),
      rows: [[t('clo.operatingIncomeSection'), fmt(income.operatingIncome)], [t('clo.operatingCostSection'), fmt(income.operatingCosts)], [t('clo.profitBeforeTax'), fmt(income.resultBeforeTax)], [t('clo.resultForYear'), fmt(income.annualResult)]],
    },
    {
      id: 'balance',
      title: t('clo.balanceSheet'),
      statusCode: balanceOk ? 'ready' : 'deviation',
      status: balanceOk ? t('clo.statusReady', 'Klar') : t('clo.statusDeviation', 'Avvik'),
      rows: [[t('clo.totalAssets'), fmt(balance.totalAssets)], [t('clo.totalEquity'), fmt(balance.totalEquity)], [t('clo.totalDebt'), fmt(balance.totalDebt)], [t('clo.balanceDiscrepancy'), fmt(balance.difference || 0)]],
    },
    {
      id: 'notes',
      title: t('clo.notes'),
      statusCode: notes.length ? 'noteCount' : 'noNotes',
      status: notes.length ? t('clo.noteCount', '{{count}} noter', { count: notes.length }) : t('clo.statusNoNotes', 'Ingen noter'),
      layout: 'list',
      rows: notes.map((note) => [note.title, note.description || (note.id === 'work-years' ? `${t('pay.fteShort')} ${workYearsLabel}` : '-')]),
    },
  ];
  const allSummaryCardById = (id) => allSummaryCards.find((card) => card.id === id);
  const renderAllDetailRows = (rows) => <div className="cl-detail">{rows.map(([label, value]) => <DetailRow key={`${label}-${value}`} label={label} value={value} />)}</div>;
  const renderAllDetails = () => {
    if (!allDetails) return null;
    const card = allSummaryCardById(allDetails);
    if (!card) return null;
    if (allDetails === 'income') return <div className="cl-all-modal">
      {renderAllDetailRows(card.rows)}
      <div className="cl-subhead">{t('home.gauge.income')}</div>
      {renderRows([...(income.salesIncomeRows || []), ...(income.otherOperatingIncomeRows || [])], t('clo.noIncomeAccountsList', 'Ingen inntektskontoer.'))}
      <div className="cl-subhead">{t('clo.costsAndTax')}</div>
      {renderRows([...(income.productCostRows || []), ...(income.payrollCostRows || []), ...(income.depreciationRows || []), ...(income.otherOperatingCostRows || []), ...(income.financeCostRows || []), ...(income.taxRows || [])], t('clo.noCostAccountsList', 'Ingen kostnadskontoer.'))}
    </div>;
    if (allDetails === 'balance') return <div className="cl-all-modal">
      {renderAllDetailRows(card.rows)}
      <div className="cl-subhead">{t('asset.title')}</div>
      {(balance.fixedAssetGroups || []).concat(balance.currentAssetGroups || []).map((group) => <React.Fragment key={group.key || group.label}>
        <Line label={group.label} value={fmt(group.total)} strong />
        {renderRows(group.rows || [])}
      </React.Fragment>)}
      <div className="cl-subhead">{t('clo.equityAndDebt')}</div>
      {(balance.equityGroups || []).concat(balance.debtGroups || []).map((group) => <React.Fragment key={group.key || group.label}>
        <Line label={group.label} value={fmt(group.total)} strong />
        {renderRows(group.rows || [])}
      </React.Fragment>)}
    </div>;
    if (allDetails === 'notes') return <div className="cl-all-modal">
      {notes.map((note, index) => <div key={note.id || note.title} className="cl-all-note">
        <h4><span className="cl-note-num">{index + 1}</span>{note.title}</h4>
        {note.description ? <p>{note.description}</p> : null}
        {Array.isArray(note.rows) && note.rows.length ? <div className="cl-note-lines">{note.rows.map((row) => <Line key={`${note.id}-${row.label}`} label={row.label} value={fmt(row.amount)} />)}</div> : null}
        {note.id === 'going-concern' && goingConcernAssessment === 'uncertainty' ? <p><b>{t('clo.assessment')}</b> {(GOING_CONCERN_REASONS.find((reason) => reason.value === goingConcernReason)?.label) || goingConcernReason}{goingConcernReason === 'Egen tekst' && goingConcernComment ? ` - ${goingConcernComment}` : ''}</p> : null}
        {note.id === 'group' ? <p><b>{t('clo.group')}</b> {t('clo.parentCompanyLabel', 'Morselskap')} {groupParent ? t('common.yes') : t('common.no')}, {t('clo.subsidiaryCompanyLabel', 'datterselskap')} {groupSubsidiary ? t('common.yes') : t('common.no')}</p> : null}
        {note.id === 'work-years' ? <p><b>{t('pay.fteShort')}</b> {workYearsLabel}</p> : null}
      </div>)}
    </div>;
    return renderAllDetailRows(card.rows);
  };

  if (!annualYearClosed) {
    return <div>
      {window.ImportedComplianceNotice&&<window.ImportedComplianceNotice year={year} area="annualAccounts"/>}
      <Card className="cl-tax-intro">
        <PageHeader className="cl-page-head" title={t('clo.annualAccountsTitle', 'Årsregnskap {{year}}', { year })} subtitle={t('clo.yearClosingRequiredForAnnual')} card={false}/>
        <div className="ph-sep"/>
        <InfoBox title={t('clo.yearClosingRequiredTitle')}>{t('clo.yearClosingRequiredBody').replace('{year}', year).replace('{nextYear}', Number(year) + 1)}</InfoBox>
        <Button className="mt-20" icon="arrowRight" onClick={() => { if (setYear) setYear(year); setRoute ? setRoute('arsavslutning') : window.plummeNav && window.plummeNav('arsavslutning'); }}>{t('clo.goToYearClosing')}</Button>
      </Card>
    </div>;
  }

  return <div>
    {window.ImportedComplianceNotice&&<window.ImportedComplianceNotice year={year} area="annualAccounts"/>}
    <PageHeader className="cl-page-head" title={t('clo.annualAccountsTitle', 'Årsregnskap {{year}}', { year })} subtitle={t('clo.annualAccountsDraftSubtitle', 'Utkast til årsregnskap for {{company}}, klart for dokumenter, signering og senere Altinn/Brønnøysund-innsending.', { company: company.name })}/>
    <div className="cl-layout">
      <Card className="cl-main">
        <StepAccordion steps={AA_STEPS} active={active} completed={displayedDone} onSelect={updateActive}
          onBack={back} onNext={next} backDisabled={idx === 0}
          nextLabel={active === 'all' ? t('clo.reviewDone') : t('clo.continueLabel')}>
          {active === 'about' && <>
            <InfoBox title={t('clo.aboutAnnualAccounts')}>{t('clo.aboutAnnualAccountsBody', 'Her samler Plumme et årsregnskapsutkast bygget på bokføringen, årsavslutningen og saldobalansen. Gå gjennom og bekreft at opplysningene stemmer.')}</InfoBox>
            {model.isEnk && <InfoBox title={t('clo.noAnnualAccountsDutyEnk')}>{t('clo.noAnnualAccountsDutyEnkBody', 'Enkeltpersonforetak leverer normalt ikke årsregnskap til Brønnøysund. Plumme viser likevel tallgrunnlaget fra bokføringen slik at skattemelding og årsavslutning henger sammen.')}</InfoBox>}
            <CheckCard title={t('clo.accountantHelpAnnual')} defaultOpen help={t('clo.accountantHelpAnnualHelp')}>
              <p>{t('clo.accountantHelpAnnualBody', 'Bruker selskapet en ekstern autorisert regnskapsfører til å fylle ut og sende inn årsregnskapet?')}</p>
              <div className="cl-decision">
                <Button size="sm" variant={rfLage === 'ja' ? 'primary' : 'secondary'} onClick={() => chooseAccountant('preparedByAccountant', 'ja')}>{t('clo.yesAccountant')}</Button>
                <Button size="sm" variant={rfLage === 'nei' ? 'primary' : 'secondary'} onClick={() => chooseAccountant('preparedByAccountant', 'nei')}>{t('clo.noAccountant')}</Button>
              </div>
            </CheckCard>
            <CheckCard title={t('clo.accountantHelpGeneral')} help={t('clo.accountantHelpGeneralHelp')}>
              <p>{t('clo.accountantHelpGeneralBody', 'Har selskapet fått hjelp av en ekstern autorisert regnskapsfører til å føre regnskapet i løpet av året?')}</p>
              <div className="cl-decision">
                <Button size="sm" variant={rfGenerelt === 'ja' ? 'primary' : 'secondary'} onClick={() => chooseAccountant('assistedByAccountant', 'ja')}>{t('clo.yesGotHelp')}</Button>
                <Button size="sm" variant={rfGenerelt === 'nei' ? 'primary' : 'secondary'} onClick={() => chooseAccountant('assistedByAccountant', 'nei')}>{t('clo.noGotHelp')}</Button>
              </div>
            </CheckCard>
            <div className="cl-box"><h4>{t('clo.theFiling')}</h4>
              <div className="cl-detail">
                <DetailRow label={t('clo.theCompany')} value={submission.companyLabel || company.name} />
                <DetailRow label={t('set.companyType')} value={model.companyType || 'AS'} />
                <DetailRow label={t('set.orgNumber')} value={company.orgnr || '-'} />
                <EditableDetailRow label={t('clo.contactEmails')} value={contactEmail} onSave={(value) => { setContactEmail(value); void saveProgress({ contactEmail: value }); }} />
                <DetailRow label={t('clo.accountingPeriod')} value={period} /><p className="muted">{t('clo.calendarOnly')}</p>{period !== `01.01.${year} - 31.12.${year}` && <Button size="sm" onClick={() => { setPeriod(`01.01.${year} - 31.12.${year}`); void saveProgress({ accountingPeriod: `01.01.${year} - 31.12.${year}` }); }}>{t('clo.resetCalendarPeriod')}</Button>}
                <DetailRow label={t('sharedText.clo.financialYear')} value={year} />
                <EditableDetailRow label={t('clo.representativeName')} value={representant} onSave={(value) => { setRepresentant(value); void saveProgress({ representativeName: value }); }} />
              </div>
            </div>
            <div className="cl-box"><h4>{t('clo.accountantAndAudit')}</h4>
              <div className="cl-detail">
                <EditableDetailRow label={t('clo.shouldBeAudited')} value={revideres} type="select" options={[{ value: 'Nei', label: t('common.no') }, { value: 'Ja', label: t('common.yes') }]} onSave={(value) => { setRevideres(value); void saveProgress({ auditorRequired: value === 'Ja' ? 'ja' : 'nei' }); }} help={t('clo.shouldBeAuditedHelp')} />
                <EditableDetailRow label={t('clo.annualAccountsRuleType')} value={submission.accountingRuleType} type="select" options={[{ value: 'Regnskapslovens regler for små foretak', label: t('clo.ruleType.smaForetak', 'Regnskapslovens regler for små foretak') }, { value: 'Regnskapslovens regler for øvrige foretak', label: t('clo.ruleType.ovrigeForetak', 'Regnskapslovens regler for øvrige foretak') }, { value: 'Forenklet IFRS', label: t('clo.ruleType.forenkletIfrs', 'Forenklet IFRS') }, { value: 'Full IFRS', label: t('clo.ruleType.fullIfrs', 'Full IFRS') }, { value: 'Bokføringsplikt uten årsregnskap', label: t('clo.ruleType.noAccountingDuty', 'Bokføringsplikt uten årsregnskap') }]} onSave={(value) => saveProgress({ accountingRuleType: value })} />
                <DetailRow label={t('clo.preparedByAccountant')} value={rfLage === 'ja' ? t('common.yes') : rfLage === 'nei' ? t('common.no') : '-'} />
                <DetailRow label={t('clo.assistedByAccountant')} value={rfGenerelt === 'ja' ? t('common.yes') : rfGenerelt === 'nei' ? t('common.no') : '-'} />
              </div>
            </div>
          </>}
          {active === 'income' && <>
            <InfoBox title={t('clo.incomeStatement')}>
              <p>{t('clo.incomeStatementInfoBody1', 'Tallene vises i hele kroner. Resultatregnskapet bygges fra bokføringen og saldobalansen for {{year}}.', { year })}</p>
              <p>{t('clo.incomeStatementInfoBody2', 'Endringer i inntekter, kostnader, finansposter eller skattekostnad må gjøres i bokføringen før årsregnskapet bekreftes.')}</p>
            </InfoBox>
            <div className="cl-subhead" style={{ marginTop: 8 }}>{t('clo.operatingIncomeSection')}</div>
            <Line label={t('clo.salesIncome')} value={fmt((income.salesIncomeRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} help={t('clo.salesIncomeHelp')} />
            {(income.otherOperatingIncomeRows || []).length ? <Line label={t('clo.otherOperatingIncome')} value={fmt((income.otherOperatingIncomeRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} /> : null}
            <Line label={t('clo.totalOperatingIncome2')} value={fmt(income.operatingIncome)} strong />
            {renderAccountDetails(t('clo.accountDetailsIncome', 'Kontodetaljer for driftsinntekter'), income.incomeRows, t('clo.noOperatingIncomeBooked', 'Ingen driftsinntekter er bokført for året.'))}

            <div className="cl-subhead">{t('clo.operatingCostSection')}</div>
            <Line label={t('clo.goodsCost')} value={fmt((income.productCostRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} help={t('clo.goodsCostHelp')} />
            {(income.payrollCostRows || []).length ? <Line label={t('clo.payrollCost')} value={fmt((income.payrollCostRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} help={t('clo.payrollCostHelp')} /> : null}
            <Line label={t('clo.depreciationFixedAssets')} value={fmt((income.depreciationRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} help={t('clo.depreciationHelp')} />
            <Line label={t('clo.otherOperatingCost')} value={fmt((income.otherOperatingCostRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} />
            <Line label={t('clo.totalOperatingCost2')} value={fmt(income.operatingCosts)} strong />
            <Line label={t('clo.operatingResult')} value={fmt(income.operatingResult)} strong />
            {renderAccountDetails(t('clo.accountDetailsCosts', 'Kontodetaljer for driftskostnader'), income.costRows, t('clo.noOperatingCostBooked', 'Ingen driftskostnader er bokført for året.'))}

            <div className="cl-subhead">{t('clo.financialItems')}</div>
            <Line label={t('clo.otherInterestIncome')} value={fmt((income.financeIncomeRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} />
            <Line label={t('clo.otherInterestCost')} value={fmt((income.financeCostRows || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))} />
            <Line label={t('clo.netFinancial')} value={fmt(income.netFinance)} />
            {renderAccountDetails(t('clo.accountDetailsFinance', 'Kontodetaljer for finansposter'), [...(income.financeIncomeRows || []), ...(income.financeCostRows || [])], t('clo.noFinanceItemsBalance', 'Ingen finansposter i saldobalansen.'))}

            <Line label={t('clo.profitBeforeTax')} value={fmt(income.resultBeforeTax)} strong />
            <Line label={t('clo.taxCost')} value={fmt(income.taxCost)} help={t('clo.taxCostHelp')} />
            {(income.taxRows || []).length ? renderAccountDetails(t('clo.accountDetailsTax', 'Kontodetaljer for skattekostnad'), income.taxRows, t('clo.noTaxCostBooked', 'Ingen skattekostnad er bokført.')) : null}
            <Line label={t('clo.resultForYear')} value={fmt(income.annualResult)} strong help={t('clo.resultAfterFinanceHelp')} />

            <div className="cl-subhead" style={{ marginTop: 16 }}>{t('clo.transfersAndAllocations')}</div>
            {(income.dispositionRows || []).map((row) => <Line key={row.id || row.label} label={row.label} value={fmt(row.amount)} />)}
            <Line label={t('clo.totalTransfers')} value={fmt(income.annualResult)} strong />
          </>}
          {active === 'balance' && <>
            <InfoBox title={t('clo.balanceSheet')}>
              <p>{t('clo.balanceSheetInfoBody1', 'Balanseoppstilling per 31.12.{{year}}. Eiendeler skal være lik egenkapital og gjeld.', { year })}</p>
              <p>{t('clo.balanceSheetInfoBody2', 'Balansen hentes fra saldobalansen etter årsavslutningen. Reelle ubalanser må rettes i bokføringen.')}</p>
            </InfoBox>
            <div className="cl-subhead">{t('clo.balanceAssets')}</div>
            <div className="cl-subhead" style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>{t('clo.fixedAssets')}</div>
            {(balance.fixedAssetGroups || []).length
              ? (balance.fixedAssetGroups || []).map((group) => renderBalanceGroup(group))
              : <p className="muted">{t('clo.noFixedAssets')}</p>}
            <Line label={t('clo.totalFixedAssets')} value={fmt(balance.totalFixedAssets)} strong />
            <div className="cl-subhead" style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>{t('clo.currentAssets')}</div>
            {(balance.currentAssetGroups || []).length
              ? (balance.currentAssetGroups || []).map((group) => renderBalanceGroup(group))
              : <p className="muted">{t('clo.noCurrentAssets')}</p>}
            <Line label={t('clo.totalCurrentAssets')} value={fmt(balance.totalCurrentAssets)} strong />
            <Line label={t('clo.totalAssets')} value={fmt(balance.totalAssets)} strong />

            <div className="cl-subhead" style={{ marginTop: 16 }}>{t('clo.balanceEquityDebt')}</div>
            <div className="cl-subhead" style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>{t('clo.equity')}</div>
            {(balance.equityGroups || []).length
              ? (balance.equityGroups || []).map((group) => renderBalanceGroup(group))
              : <p className="muted">{t('clo.noEquityAccounts')}</p>}
            <Line label={t('clo.totalEquity')} value={fmt(balance.totalEquity)} strong />
            <div className="cl-subhead" style={{ fontWeight: 700, marginTop: 8, marginBottom: 4 }}>{t('clo.debt')}</div>
            {(balance.debtGroups || []).length
              ? (balance.debtGroups || []).map((group) => renderBalanceGroup(group))
              : <p className="muted">{t('clo.noDebtAccounts')}</p>}
            <Line label={t('clo.totalDebt')} value={fmt(balance.totalDebt)} strong />
            <Line label={t('clo.totalEquityAndDebt')} value={fmt(balance.totalEquityAndDebt)} strong />
            {Math.abs(balance.difference || 0) > 0.5 && <InfoBox title={t('clo.balanceDiscrepancy')}>{t('clo.annualBalanceDiscrepancyBody', 'Eiendeler og egenkapital/gjeld avviker med {{difference}}. Rett avviket i bokføringen før innsending.', { difference: fmt(balance.difference) })}</InfoBox>}
          </>}
          {active === 'notes' && <>
            <InfoBox title={t('clo.notes')}>{t('clo.notesInfoBody', 'Plumme foreslår noter basert på selskapstype og kontoaktivitet. Se over og bekreft innholdet.')}</InfoBox>

            {principlesNote && <div className="cl-box">
              <h4><span className="cl-note-num">1</span>{principlesNote.title}</h4>
              <p>{principlesNote.description}</p>
              {principlesOpen && <div className="cl-note-text">
                {(principlesNote.details || []).map((text) => <p key={text}>{text}</p>)}
              </div>}
              <Button size="sm" variant="ghost" onClick={() => setPrinciplesOpen((value) => !value)}>{principlesOpen ? t('clo.hidePrinciples', 'Skjul prinsipper') : t('clo.showAccountingPrinciples', 'Vis regnskapsprinsipper')}</Button>
            </div>}

            {goingConcernNote && <div className="cl-box">
              <h4><span className="cl-note-num">2</span>{goingConcernNote.title}</h4>
              <p>{goingConcernNote.description}</p>
              <div className="cl-detail cl-note-control">
                <div className="cl-detail-row">
                  <div className="cl-detail-left"><span>{t('clo.assessmentType')}</span></div>
                  <div className="cl-note-actions">
                    <Button size="sm" variant={goingConcernAssessment === 'no_uncertainty' ? 'primary' : 'secondary'} onClick={() => chooseGoingConcern('no_uncertainty')}>{t('clo.noUncertainty')}</Button>
                    <Button size="sm" variant={goingConcernAssessment === 'uncertainty' ? 'primary' : 'secondary'} onClick={() => chooseGoingConcern('uncertainty')}>{t('clo.goingConcernUncertainty')}</Button>
                  </div>
                </div>
              </div>
              {goingConcernAssessment === 'uncertainty' && <div className="cl-note-select">
                <p className="t-sm muted">{t('clo.uncertaintyFreeText')}</p>
                <select className="input" value={goingConcernReason} onChange={(event) => chooseGoingConcernReason(event.target.value)}>
                  {GOING_CONCERN_REASONS.map((reason) => <option key={reason.value} value={reason.value}>{reason.label}</option>)}
                </select>
                {goingConcernReason === 'Egen tekst' && <textarea className="input cl-note-textarea" rows={4} value={goingConcernComment} onChange={(event) => updateGoingConcernComment(event.target.value)} placeholder={t('clo.notePh')} />}
              </div>}
            </div>}

            {fixedAssetsNote && <div className="cl-box">
              <h4><span className="cl-note-num">3</span>{fixedAssetsNote.title}</h4>
              <div className="cl-detail">
                {(fixedAssetsNote.rows || []).map((row) => <DetailRow key={row.label} label={row.label} value={fmt(row.amount)} />)}
              </div>
            </div>}

            {groupNote && <div className="cl-box">
              <h4><span className="cl-note-num">4</span>{groupNote.title}</h4>
              <div className="cl-decision cl-note-decision">
                <Button size="sm" variant={groupParent ? 'primary' : 'secondary'} onClick={toggleGroupParent}>{t('clo.isParentCompanyLabel', 'Er morselskap:')} {groupParent ? t('common.yes') : t('common.no')}</Button>
                <Button size="sm" variant={groupSubsidiary ? 'primary' : 'secondary'} onClick={toggleGroupSubsidiary}>{t('clo.isSubsidiaryCompanyLabel', 'Er datterselskap:')} {groupSubsidiary ? t('common.yes') : t('common.no')}</Button>
              </div>
              {(groupParent || groupSubsidiary || (groupNote.rows || []).some((row) => Math.abs(Number(row.amount || 0)) > 0.005)) && <div className="cl-detail">
                {(groupNote.rows || []).map((row) => <DetailRow key={row.label} label={row.label} value={fmt(row.amount)} />)}
              </div>}
            </div>}

            {workYearsNote && <div className="cl-box">
              <h4><span className="cl-note-num">5</span>{workYearsNote.title}</h4>
              <p>{workYearsNote.description}</p>
              <div className="cl-detail cl-note-control">
                <div className="cl-detail-row">
                  <div className="cl-detail-left"><span>{t('clo.fteCount')}</span></div>
                  <div className="cl-note-actions">
                    <Button size="sm" variant={workYears <= 0 ? 'primary' : 'secondary'} onClick={() => setAnnualWorkYears(0)}>{t('clo.noEmployees')}</Button>
                    <Button size="sm" variant={workYears > 0 ? 'primary' : 'secondary'} onClick={incrementAnnualWorkYears}>{t('clo.weHadEmployeesButton', 'Vi har hatt ansatte +')}</Button>
                    {workYears > 0 && <strong className="cl-note-workyears">{workYearsLabel}</strong>}
                  </div>
                </div>
              </div>
            </div>}
            {window.AnnualAccountsDisclosureEditor ? <window.AnnualAccountsDisclosureEditor year={year}
              process={process} previousTrialRows={model.previousTrialRows || []}
              noteRuleContext={model.annualNoteRuleContext || {}}
              onSave={(patch) => window.PlummeBackend.saveAnnualAccountsProgress({ year, patch })}
            /> : null}
          </>}
          {active === 'all' && <>
            <InfoBox title={t('clo.allInformation')}>{t('clo.allInformationInfoBody', 'Her får du en samlet kontrollside for årsregnskapet. Hvert tema viser nøkkelopplysninger først, med detaljer i egen dialog.')}</InfoBox>
            <div className="cl-all-grid">
              {allSummaryCards.map((card) => <div key={card.id} className="cl-all-card">
                <div className="cl-all-card-h">
                  <div>
                    <h4>{card.title}</h4>
                    <span className={'cl-status-pill ' + (card.statusCode === 'deviation' || card.statusCode === 'missingInfo' || card.statusCode === 'missingChoice' ? 'todo' : 'done')}><span className="dot"></span>{card.status}</span>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => setAllDetails(card.id)}>{t('clo.viewDetails')}</Button>
                </div>
                {card.layout === 'list'
                  ? <div className="cl-all-list">
                    {card.rows.slice(0, 4).map(([label, value]) => <div key={`${card.id}-${label}`} className="cl-all-list-row">
                      <strong>{label}</strong><span>{value}</span>
                    </div>)}
                  </div>
                  : <div className="cl-all-facts">
                    {card.rows.slice(0, 4).map(([label, value]) => <React.Fragment key={`${card.id}-${label}`}>
                      <span>{label}</span><strong>{value}</strong>
                    </React.Fragment>)}
                  </div>}
              </div>)}
            </div>
            {!balanceOk && <InfoBox title={t('clo.balanceDiscrepancy')}>{t('clo.balanceDiscrepancyReviewBody', 'Eiendeler og egenkapital/gjeld avviker med {{difference}}. Rett avviket i bokføringen før innsending markeres ferdig.', { difference: fmt(balance.difference) })}</InfoBox>}
            <Modal open={Boolean(allDetails)} onClose={() => setAllDetails(null)} title={allSummaryCardById(allDetails)?.title || t('clo.detailsModalFallbackTitle', 'Detaljer')} max={760}
              footer={<Button variant="secondary" onClick={() => setAllDetails(null)}>{t('clo.close')}</Button>}>
              {renderAllDetails()}
            </Modal>
          </>}
        </StepAccordion>
      </Card>

      <Rail title={t('nav.annualAccounts')} steps={AA_RAIL} stage={stage} mascotImg={AA_RAIL_IMAGES[stage] || 'src/img/plommeMagnifier.png'} mascotStyle={AA_RAIL_IMAGE_STYLES[stage] || AA_RAIL_IMAGE_STYLE}>
        {stage === 0 && <p>{t('clo.allInformationHelp')}</p>}
        {stage >= 1 && stage <= 3 && <window.AnnualDocumentsPanel year={year} model={model} stage={stage} onStage={moveStage} companyRoles={companyRoles} shareholders={shareholders} annualMeetings={annualMeetings} />}
        {stage === 4 && <div className="cl-annual-done">
          <p>{t('clo.annualMarkedFiled')}</p>
          <div className="cl-rail-actions"><Button variant="secondary" icon="refresh" onClick={reopenAnnualAccounts}>{t('clo.cancelAnnualAccounts')}</Button></div>
        </div>}
      </Rail>
    </div>
  </div>;
}
Object.assign(window, { SkattemeldingPage, ArsregnskapPage });
